import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import {
  ProjectDetail,
  ProjectFormData,
  FormErrors,
  TeamMember,
  ModalFieldConfig,
  projectModalFields,
  ProjectDrawing,
  RETAINING_STANDARD_OPTIONS,
  RWC_REGION_OPTIONS,
  PROJECT_SITE_CLASSIFICATION_OPTIONS,
  PROJECT_WALL_TYPE_OPTIONS,
  PROJECT_ROOF_TYPE_OPTIONS,
  PROJECT_FLOOR_TYPE_OPTIONS,
  PROJECT_STRUCTURE_TYPE_OPTIONS,
  nccClassesFor,
  MultiSelectField,
  Inspection,
  InspectionFormData,
  InspectionEngineer,
  QaItem,
  RWC_QA_ITEMS,
  DrawingIndexEntry,
  ProjectRisk,
  ProgrammeItem,
  DESIGN_RISK_ITEMS,
  RISK_RATING_OPTIONS,
  ProjectDetailTab,
  StageInfo,
  JOB_TYPE_HINTS,
  ProjectJobType
} from './config';
import StageContactField from '../StageContactField';
import { FEE_PHASES, normDiscipline } from '../../modules/InvoiceRegister/config';
import { useCreateDocument } from '../../modules/Documents/useCreateDocument';
import { AvailableTemplate } from '../../modules/Documents/config';
import Modal from '../Modal';
import Dropdown from '../Dropdown';
import { DropdownItem } from '../Dropdown/config';
import SearchableClientDropdown from '../SearchableClientDropdown';
import SearchableContactDropdown from '../SearchableContactDropdown';
import ContactInfoButton from '../ContactInfoButton';
import NewContactCard from '../NewContactCard';
import TagSelector from '../TagSelector';
import { TagOption } from '../TagSelector/config';
import AddressAutocomplete, { AddressFields } from '../AddressAutocomplete';
import StagesTab from './components/StagesTab';
import DocumentsTab from './components/DocumentsTab';
import { useUserRole } from '../../utils/useUserRole';
import { canUseManagerOnlyStatuses, workflowStatusDropdownItems } from '../../utils/workflowStatusAccess';
import ThreadPanel from './components/ThreadPanel';
import TeamPanel from '../TeamPanel';
import GeneralInfoPanel from '../GeneralInfoPanel';
import TabbedModalLayout, { TabDef } from '../TabbedModalLayout';
import SaveDot from '../SaveDot';
import Field from '../Field';
import TextInput from '../TextInput';
import TextArea from '../TextArea';
import Toggle from '../Toggle';
import SegmentedControl from '../SegmentedControl';
import IconButton from '../IconButton';
import {
  EditIcon, CheckIcon, CloseIcon, TrashIcon, ArrowUpIcon, ArrowDownIcon, PlusIcon, ChevronDownIcon,
  ClipboardIcon, FlagIcon, ToolIcon, LayersIcon, UsersIcon, FileTextIcon, SlidersIcon,
} from '../IconButton/icons';
import PanelHeading from '../PanelHeading';
import WorkflowStatusPill from '../WorkflowStatusPill';
import { useConfirmDialog } from '../ConfirmDialog';
import { useFieldSaveState, FieldKey } from './useFieldSaveState';
import './project-detail-modal.scss';

// =======================================
// INTERFACES
// =======================================

interface ProjectDetailModalProps {
  isOpen: boolean;
  projectId: number | null;
  stageId?: number | null;
  stageInfo?: StageInfo | null;
  /** Tab to land on when the modal opens. Omitted, it keeps its default. */
  initialTab?: ProjectDetailTab;
  onClose: () => void;
  onProjectUpdated: () => void;
  onProjectDeleted: () => void;
  teamMembers: TeamMember[];
}

// =======================================
// HELPERS
// =======================================

/** "Nicos Terpkos" -> "NT". Same rule as TeamPanel's card avatars. */
const initialsOf = (fullName: string) =>
  fullName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || '?';

// =======================================
// MAIN COMPONENT
// =======================================

const ProjectDetailModal: React.FC<ProjectDetailModalProps> = ({
  isOpen,
  projectId,
  stageId,
  initialTab,
  stageInfo,
  onClose,
  onProjectUpdated,
  onProjectDeleted,
  teamMembers
}) => {
  // isReadOnly disables create/sign actions for QA accounts, per the documented
  // rule (hasManagerView for visibility, isReadOnly for writes).
  const userRole = useUserRole();
  const { isReadOnly } = userRole;

  // State management
  const [activeTab, setActiveTab] = useState<ProjectDetailTab>(initialTab ?? 'job');
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [formData, setFormData] = useState<ProjectFormData>({
    description: '',
    status: '',
    types: '',
    standard: '',
    project_lead: '',
    client: '',
    primary_contact: '',
    wall_type: [],
    roof_type: [],
    floor_type: [],
    structure_type: [],
    wind_region: '',
    site_classification: '',
    key_words: '',
    po: '',
    // Address fields
    street_address: '',
    city: '',
    state: '',
    postcode: '',
    country: 'Australia',
    // RWC-specific fields
    retaining_standard: '',
    startup_meeting: false,
    number_of_inspections: '',
    requires_qa: false,
    region: '',
    // Contractor fields
    head_contractor_client_id: '',
    head_contractor_contact_id: '',
    wall_contractor_client_id: '',
    wall_contractor_contact_id: '',
    // Design PEP fields
    scope_of_works: '',
    exclusions: '',
    has_civil_scope: false,
    care_of: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // (Team membership + lead are handled by TeamPanel)
  
  // Stage form data
  const [stageFormData, setStageFormData] = useState({
    stage_id: '',
    stage_description: '',
    po: '',
    // RWC-specific fields
    region: '',
    retaining_standard: '',
    startup_meeting: false,
    number_of_inspections: '',
    requires_qa: false,
    // RWD-specific fields
    site_classification: '',
    // Contractor fields
    head_contractor_client_id: '',
    head_contractor_contact_id: '',
    wall_contractor_client_id: '',
    wall_contractor_contact_id: '',
    care_of: '',
    // '' inherits the project's primary contact
    primary_contact: ''
  });

  // Inline add contact: which field's form is open (the form owns its inputs)
  const [addingContactFor, setAddingContactFor] = useState<string | null>(null);
  const [contactRefreshKeys, setContactRefreshKeys] = useState<Record<string, number>>({});

  // Address accordion state
  const [showAddressDetails, setShowAddressDetails] = useState(false);
  const [siteAddressDisplay, setSiteAddressDisplay] = useState('');

  // Tags state
  const [projectTags, setProjectTags] = useState<TagOption[]>([]);
  
  // Workflow statuses for stage
  const [workflowStatuses, setWorkflowStatuses] = useState<{ id: number; status_name: string; display_order: number }[]>([]);

  // RWD Drawings state
  const [drawings, setDrawings] = useState<ProjectDrawing[]>([]);
  const [isLoadingDrawings, setIsLoadingDrawings] = useState(false);
  const [editingDrawingId, setEditingDrawingId] = useState<number | null>(null);
  const [drawingForm, setDrawingForm] = useState({ drawing_number: '', issue_number: '0', title: '' });

  // Inspections state (Construction tab)
  const [inspections, setInspections] = useState<Inspection[]>([]);
  // The number the Add form offers. Server-computed: a client-side max over the
  // list would miss a DRAFT site instruction, which holds a number with no
  // inspection row behind it yet.
  const [nextInspectionNumber, setNextInspectionNumber] = useState<number | null>(null);
  const [isLoadingInspections, setIsLoadingInspections] = useState(false);
  const [activeEngineers, setActiveEngineers] = useState<InspectionEngineer[]>([]);
  const [inspectionFormData, setInspectionFormData] = useState<InspectionFormData>({
    sequence_number: '',
    inspection_date: '',
    team_member_id: '',
    description: ''
  });
  const [isAddingInspection, setIsAddingInspection] = useState(false);
  const [editingInspectionId, setEditingInspectionId] = useState<number | null>(null);
  const [editInspectionFormData, setEditInspectionFormData] = useState<InspectionFormData>({
    sequence_number: '',
    inspection_date: '',
    team_member_id: '',
    description: ''
  });
  const [isUpdatingInspection, setIsUpdatingInspection] = useState(false);
  const [isDeletingInspection, setIsDeletingInspection] = useState<number | null>(null);
  const [inspectionError, setInspectionError] = useState<string | null>(null);
  
  // RWC completion dates local state (for typing support)
  const [localStartUpDate, setLocalStartUpDate] = useState<string>('');
  const [localQaDate, setLocalQaDate] = useState<string>('');

  // PEP QA checklist items state (current project or stage) + local date map for typing
  const [qaItems, setQaItems] = useState<QaItem[]>([]);
  const [localQaItemDates, setLocalQaItemDates] = useState<Record<string, string>>({});

  // Design PEP state (project-level)
  const [drawingIndex, setDrawingIndex] = useState<DrawingIndexEntry[]>([]);
  const [editingDrawingIndexId, setEditingDrawingIndexId] = useState<number | null>(null);
  const [risks, setRisks] = useState<ProjectRisk[]>([]);
  const [customRiskLabel, setCustomRiskLabel] = useState('');
  const [programmeItems, setProgrammeItems] = useState<ProgrammeItem[]>([]);
  const [customProgrammeName, setCustomProgrammeName] = useState('');

  // Auto-save status state
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const statusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Per-field save dots, and the debounced saves that have not fired yet.
  // One entry per field: an edit to a second field must not cancel the first
  // field's pending save, which is what a single shared timer used to do.
  const { mark: markField, stateFor: fieldSaveState } = useFieldSaveState();
  const pendingSaves = useRef<Map<string, { timer: NodeJS.Timeout; run: () => void }>>(new Map());

  /** Send every debounced save now. Called before anything that could lose them. */
  const flushPendingSaves = useCallback(() => {
    pendingSaves.current.forEach(({ timer, run }) => {
      clearTimeout(timer);
      run();
    });
    pendingSaves.current.clear();
  }, []);

  /** Drop pending saves without sending them: the record is about to go. */
  const discardPendingSaves = useCallback(() => {
    pendingSaves.current.forEach(({ timer }) => clearTimeout(timer));
    pendingSaves.current.clear();
  }, []);

  /**
   * Put the inline "add contact" panel away. Unmounting it forgets what was
   * typed in it.
   */
  const handleCancelAddContact = useCallback(() => {
    setAddingContactFor(null);
  }, []);

  /** Close, after sending whatever is still sitting in a debounce window. */
  const handleClose = useCallback(() => {
    flushPendingSaves();
    // The parent keeps this modal mounted and only stops rendering it, so
    // state outlives a close. Without this the half-typed create-contact
    // panel came back open on the next job opened -- against that job's
    // client.
    handleCancelAddContact();
    onClose();
  }, [flushPendingSaves, handleCancelAddContact, onClose]);

  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  const supabase = useSupabaseClient();

  /**
   * How to put this modal back, minus the tab.
   *
   * Built once here because only the modal knows all three: the ids, and the
   * stage title text, which is passed in from the row that was clicked and is
   * not recoverable from stageId alone.
   */
  const reopenIdentity = useMemo(
    () => ({ projectId: projectId as number, stageId: stageId ?? null, stageInfo: stageInfo ?? null }),
    [projectId, stageId, stageInfo]
  );

  /**
   * The template whose issuing records an inspection -- the site instruction.
   * Read from get_available_document_templates rather than named here, so
   * Construction Admin offers it on exactly the jobs and to exactly the roles
   * the Documents tab does, and stops offering it if that ever changes.
   */
  const [inspectionTemplate, setInspectionTemplate] = useState<AvailableTemplate | null>(null);
  /** The paper/email form, which is now the secondary way in. */
  const [isManualInspectionOpen, setIsManualInspectionOpen] = useState(false);

  const {
    startCreate: startCreateDocument,
    openDocument: openDocumentFromModal,
    dialog: createDocumentDialog,
  } = useCreateDocument({
    projectId: projectId as number,
    stageId: stageId ?? null,
    isReadOnly,
    onClose: handleClose,
    reopenIdentity,
    returnTab: 'construction',
  });

  // =======================================
  // AUTO-SAVE FUNCTIONS
  // =======================================

  /**
   * Show saved status briefly then fade to idle
   */
  const showSavedStatus = useCallback(() => {
    setSaveStatus('saved');
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
    }
    statusTimeoutRef.current = setTimeout(() => {
      setSaveStatus('idle');
    }, 2000);
  }, []);

  /**
   * Auto-save a single project field
   */
  const autoSaveProjectField = useCallback(async (fieldName: string, value: string | string[] | number | boolean | null) => {
    if (!projectId) return;

    const key: FieldKey = `project:${fieldName}`;
    setSaveStatus('saving');
    markField(key, 'saving');

    try {
      const updateData: Record<string, string | string[] | number | boolean | null> = {};
      
      // Handle special field types
      if (fieldName === 'number_of_inspections') {
        updateData[fieldName] = value ? parseInt(value as string, 10) : null;
      } else if (fieldName === 'startup_meeting' || fieldName === 'requires_qa') {
        updateData[fieldName] = value as boolean;
      } else if (value === '') {
        updateData[fieldName] = null;
      } else {
        updateData[fieldName] = value;
      }

      const { data, error } = await supabase
        .rpc('update_project_detail', {
          project_id: projectId,
          project_data: updateData
        });

      if (error) {
        throw new Error(error.message);
      }

      if (data && !data.success) {
        throw new Error(data.error || 'Update failed');
      }

      showSavedStatus();
      markField(key, 'saved');
      onProjectUpdated();
    } catch (err) {
      console.error('Error auto-saving project field:', err);
      setSaveStatus('error');
      markField(key, 'error');
    }
  }, [projectId, supabase, showSavedStatus, onProjectUpdated, markField]);

  /**
   * Auto-save project tags
   */
  const autoSaveProjectTags = useCallback(async (tags: TagOption[]) => {
    if (!projectId) return;

    setSaveStatus('saving');
    markField('project:tags', 'saving');

    try {
      const tagIds = tags.map(t => t.id);
      const { error } = await supabase.rpc('update_project_tags', {
        p_project_id: projectId,
        p_tag_ids: tagIds
      });

      if (error) {
        throw new Error(error.message);
      }

      showSavedStatus();
      markField('project:tags', 'saved');
      onProjectUpdated();
    } catch (err) {
      console.error('Error auto-saving tags:', err);
      setSaveStatus('error');
      markField('project:tags', 'error');
    }
  }, [projectId, supabase, showSavedStatus, onProjectUpdated, markField]);

  const handleProjectAddressSelected = useCallback(async (address: AddressFields) => {
    setFormData(prev => ({
      ...prev,
      street_address: address.street_address,
      city: address.city,
      state: address.state,
      postcode: address.postcode,
      country: address.country,
    }));

    setSiteAddressDisplay(
      address.mapbox_data?.full_address ||
      [
        address.street_address,
        [address.city, address.state, address.postcode].filter(Boolean).join(' '),
        address.country && address.country !== 'Australia' ? address.country : null
      ].filter(Boolean).join(', ')
    );

    if (!projectId) return;
    setSaveStatus('saving');
    markField('project:site_address', 'saving');

    try {
      const { data, error } = await supabase.rpc('update_project_detail', {
        project_id: projectId,
        project_data: {
          street_address: address.street_address || null,
          city: address.city || null,
          state: address.state || null,
          postcode: address.postcode || null,
          country: address.country || null,
          latitude: address.latitude,
          longitude: address.longitude,
          mapbox_data: address.mapbox_data,
        }
      });

      if (error) throw new Error(error.message);
      if (data && !data.success) throw new Error(data.error || 'Update failed');

      showSavedStatus();
      markField('project:site_address', 'saved');
      onProjectUpdated();
    } catch (err) {
      console.error('Error saving project address:', err);
      setSaveStatus('error');
      markField('project:site_address', 'error');
    }
  }, [projectId, supabase, showSavedStatus, onProjectUpdated, markField]);

  /**
   * Auto-save a single stage field
   */
  const autoSaveStageField = useCallback(async (fieldName: string, value: string | number | boolean | null) => {
    if (!stageId) return;

    const key: FieldKey = `stage:${fieldName}`;
    setSaveStatus('saving');
    markField(key, 'saving');

    try {
      const updateData: Record<string, string | number | boolean | null> = {};
      
      // Handle special field types
      if (fieldName === 'number_of_inspections') {
        updateData[fieldName] = value ? parseInt(value as string, 10) : null;
      } else if (fieldName === 'startup_meeting' || fieldName === 'requires_qa') {
        updateData[fieldName] = value as boolean;
      } else if (value === '') {
        updateData[fieldName] = null;
      } else {
        updateData[fieldName] = value;
      }

      const { data, error } = await supabase
        .rpc('update_project_stage', {
          p_stage_id: stageId,
          p_stage_data: updateData
        });

      if (error) {
        throw new Error(error.message);
      }

      if (data && !data.success) {
        throw new Error(data.error || 'Update failed');
      }

      showSavedStatus();
      markField(key, 'saved');
      onProjectUpdated();
    } catch (err) {
      console.error('Error auto-saving stage field:', err);
      setSaveStatus('error');
      markField(key, 'error');
    }
  }, [stageId, supabase, showSavedStatus, onProjectUpdated, markField]);

  /**
   * Debounce a text field's or multi-select's save. One timer per field key (see pendingSaves):
   * typing in a second field within the window no longer cancels the first.
   */
  const scheduleSave = useCallback((key: FieldKey, run: () => void) => {
    const prev = pendingSaves.current.get(key);
    if (prev) clearTimeout(prev.timer);
    markField(key, 'dirty');
    const timer = setTimeout(() => {
      pendingSaves.current.delete(key);
      run();
    }, 500);
    pendingSaves.current.set(key, { timer, run });
  }, [markField]);

  const debouncedSaveProjectField = useCallback((fieldName: string, value: string | string[] | null) => {
    scheduleSave(`project:${fieldName}`, () => { void autoSaveProjectField(fieldName, value); });
  }, [scheduleSave, autoSaveProjectField]);

  const debouncedSaveStageField = useCallback((fieldName: string, value: string) => {
    scheduleSave(`stage:${fieldName}`, () => { void autoSaveStageField(fieldName, value); });
  }, [scheduleSave, autoSaveStageField]);

  // On unmount -- a route change, such as opening a site instruction -- send
  // the pending saves rather than dropping them. The RPC finishing after
  // unmount is harmless; the setState that follows is a no-op.
  useEffect(() => {
    return () => {
      flushPendingSaves();
      if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
    };
  }, [flushPendingSaves]);

  // =======================================
  // DATA FETCHING
  // =======================================

  const fetchProjectDetail = useCallback(async (silent = false) => {
    if (!projectId) return;

    // Only show loading state for initial load, not for silent refreshes
    if (!silent) {
    setIsLoading(true);
    setErrors({});
    }

    try {
      const { data, error } = await supabase
        .rpc('get_project_detail', { p_project_id: projectId });

      if (error) {
        throw new Error(`Failed to fetch project details: ${error.message}`);
      }

      if (data.error) {
        throw new Error(data.error);
      }

      setProjectDetail(data);
      
      // Only initialize form data on initial load, not on silent refreshes
      if (!silent) {
      setFormData({
        description: data.description || '',
        status: data.status || '',
        types: data.types || '',
        standard: data.standard || '',
        project_lead: data.project_lead?.id?.toString() || '',
        client: data.client?.id?.toString() || '',
        primary_contact: data.primary_contact?.id?.toString() || '',
        wall_type: data.wall_type ?? [],
        roof_type: data.roof_type ?? [],
        floor_type: data.floor_type ?? [],
        structure_type: data.structure_type ?? [],
        wind_region: data.wind_region || '',
        site_classification: data.site_classification || '',
        key_words: data.key_words || '',
        po: data.po || '',
        street_address: data.street_address || '',
        city: data.city || '',
        state: data.state || '',
        postcode: data.postcode || '',
        country: data.country || 'Australia',
        retaining_standard: data.retaining_standard || '',
        startup_meeting: data.startup_meeting || false,
        number_of_inspections: data.number_of_inspections?.toString() || '',
        requires_qa: data.requires_qa || false,
        region: data.region || '',
        head_contractor_client_id: data.head_contractor?.client?.id?.toString() || '',
        head_contractor_contact_id: data.head_contractor?.contact?.id?.toString() || '',
        wall_contractor_client_id: data.wall_contractor?.client?.id?.toString() || '',
        wall_contractor_contact_id: data.wall_contractor?.contact?.id?.toString() || '',
        scope_of_works: data.scope_of_works || '',
        exclusions: data.exclusions || '',
        has_civil_scope: data.has_civil_scope || false,
        care_of: data.care_of || ''
      });

      if (data.mapbox_data?.full_address) {
        setSiteAddressDisplay(data.mapbox_data.full_address);
      } else {
        const parts = [
          data.street_address,
          [data.city, data.state, data.postcode].filter(Boolean).join(' '),
          data.country && data.country !== 'Australia' ? data.country : null
        ].filter(Boolean);
        setSiteAddressDisplay(parts.join(', '));
      }
      }

    } catch (err) {
      console.error('Error fetching project detail:', err);
      setErrors({ general: err instanceof Error ? err.message : 'Failed to load project details' });
    } finally {
      if (!silent) {
      setIsLoading(false);
      }
    }
  }, [projectId, supabase]);

  /**
   * Fetch tags for the current project
   */
  const fetchProjectTags = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const { data, error } = await supabase.rpc('get_project_tags', {
        p_project_id: projectId
      });
      
      if (error) {
        console.error('Error fetching project tags:', error);
        return;
      }
      
      setProjectTags(data || []);
    } catch (err) {
      console.error('Error fetching project tags:', err);
    }
  }, [projectId, supabase]);

  /**
   * Fetch active engineers for the inspections dropdown
   */
  const fetchActiveEngineers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .rpc('search_active_engineers', { search_term: null, limit_results: 100 });
      
      if (error) {
        console.error('Error fetching active engineers:', error);
        return;
      }
      
      setActiveEngineers(data || []);
    } catch (err) {
      console.error('Error fetching active engineers:', err);
    }
  }, [supabase]);

  /**
   * Fetch inspections for the current project or stage
   */
  const fetchInspections = useCallback(async () => {
    // Determine which entity to fetch inspections for
    const isStageView = !!stageId;
    
    setIsLoadingInspections(true);
    setInspectionError(null);
    
    try {
      const rpcName = isStageView ? 'get_stage_inspections' : 'get_project_inspections';
      const rpcParams = isStageView ? { p_stage_id: stageId } : { p_project_id: projectId };
      
      const { data, error } = await supabase.rpc(rpcName, rpcParams);
      
      if (error) {
        throw new Error(error.message);
      }
      
      if (data && !data.success) {
        throw new Error(data.error || 'Failed to fetch inspections');
      }
      
      setInspections(data?.inspections || []);
      // Offer the next number in the Add form. Safe to overwrite what is there:
      // inspections are re-fetched when the tab opens and after every add, edit
      // or delete -- each a point where the form is starting fresh anyway.
      const next: number | null = data?.next_sequence_number ?? null;
      setNextInspectionNumber(next);
      setInspectionFormData(prev => ({
        ...prev,
        sequence_number: next != null ? String(next) : '',
      }));
    } catch (err) {
      console.error('Error fetching inspections:', err);
      setInspectionError(err instanceof Error ? err.message : 'Failed to load inspections');
    } finally {
      setIsLoadingInspections(false);
    }
  }, [projectId, stageId, supabase]);

  // Load project detail when modal opens
  useEffect(() => {
    if (isOpen && projectId) {
      fetchProjectDetail();
      fetchProjectTags();
    }
  }, [isOpen, projectId, fetchProjectDetail, fetchProjectTags]);

  // Land on the tab the caller asked for each time the modal opens. The modal
  // stays mounted between opens, so without this a reopen (returning from the
  // document editor, say) would show whichever tab was last looked at.
  useEffect(() => {
    if (isOpen && initialTab) setActiveTab(initialTab);
  }, [isOpen, initialTab]);

  // Viewing a stage swaps the project PEP tab for the stage one, and vice versa.
  //
  // Functional updates, not a read of `activeTab`: this effect runs only when
  // stageId changes, so a closure over `activeTab` holds whatever it was one
  // render earlier. That went unnoticed until the modal started being reopened
  // on a named tab -- the effect fired on the same commit, read a stale 'job',
  // and flipped a request for Documents to Stage PEP. `prev` sees the value the
  // effect above has already queued.
  useEffect(() => {
    if (stageId) {
      setActiveTab(prev => (prev === 'stages' || prev === 'job' ? 'stage' : prev));
    } else {
      setActiveTab(prev => (prev === 'stage' ? 'job' : prev));
    }
  }, [stageId]);

  // =======================================
  // FORM HANDLING
  // =======================================

  const handleDelete = useCallback(async () => {
    // Anything still in a debounce window is for the record being deleted.
    discardPendingSaves();
    // For stages, delete the stage; for projects, delete the project
    if (stageId) {
      setIsDeleting(true);
      setErrors({});

      try {
        const { data, error } = await supabase
          .rpc('delete_project_stage', { p_stage_id: stageId });

        if (error) {
          throw new Error(`Failed to delete stage: ${error.message}`);
        }

        if (data && !data.success) {
          throw new Error(data.error || 'Failed to delete stage');
        }

        // Success - notify parent and close modal
        onProjectDeleted();
        onClose();

      } catch (err) {
        console.error('Error deleting stage:', err);
        setErrors({
          general: err instanceof Error ? err.message : 'Failed to delete stage'
        });
      } finally {
        setIsDeleting(false);
      }
    } else if (projectId) {
    setIsDeleting(true);
    setErrors({});

    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (error) {
        throw new Error(`Failed to delete project: ${error.message}`);
      }

      // Success - notify parent and close modal
      onProjectDeleted();
      onClose();

    } catch (err) {
      console.error('Error deleting project:', err);
      setErrors({
        general: err instanceof Error ? err.message : 'Failed to delete project'
      });
    } finally {
      setIsDeleting(false);
    }
    }
  }, [projectId, stageId, supabase, onProjectDeleted, onClose, discardPendingSaves]);

  const handleInputChange = useCallback((field: keyof ProjectFormData, value: string, isDropdown = false) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'client' && value !== prev.client) {
        updated.primary_contact = '';
      }
      if (field === 'head_contractor_client_id' && value !== prev.head_contractor_client_id) {
        updated.head_contractor_contact_id = '';
      }
      if (field === 'wall_contractor_client_id' && value !== prev.wall_contractor_client_id) {
        updated.wall_contractor_contact_id = '';
      }
      return updated;
    });
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }

    if (isDropdown) {
      autoSaveProjectField(field, value);
      if (field === 'client') {
        autoSaveProjectField('primary_contact', '');
      }
      if (field === 'head_contractor_client_id') {
        autoSaveProjectField('head_contractor_contact_id', '');
      }
      if (field === 'wall_contractor_client_id') {
        autoSaveProjectField('wall_contractor_contact_id', '');
      }
    } else {
      debouncedSaveProjectField(field, value);
    }
  }, [errors, autoSaveProjectField, debouncedSaveProjectField]);

  // =======================================
  // DROPDOWN ITEMS
  // =======================================

  const teamMemberDropdownItems = useMemo<DropdownItem[]>(() => {
    return [
      { id: '', label: 'None', value: '' },
      ...teamMembers.map(member => ({
        id: member.id.toString(),
        label: `${member.first_name} ${member.last_name}`,
        value: member.id.toString()
      }))
    ];
  }, [teamMembers]);

  // Get the current stage data if viewing a stage
  const currentStage = useMemo(() => {
    if (!stageId || !projectDetail?.stages) return null;
    return projectDetail.stages.find(s => s.id === stageId) || null;
  }, [stageId, projectDetail?.stages]);

  /** Ask, then delete. The question names the record so a slip is caught. */
  const handleDeleteRequest = useCallback(async () => {
    const name = stageId && currentStage
      ? `${currentStage.job_number} – ${currentStage.stage_description}`
      : projectDetail?.description ?? '';
    const ok = await confirm({
      title: stageId ? 'Delete this stage?' : 'Delete this project?',
      message: (
        <>
          <strong>{name}</strong> and everything recorded against it will be removed. This cannot be undone.
        </>
      ),
      confirmLabel: stageId ? 'Delete stage' : 'Delete project',
      tone: 'danger',
    });
    if (ok) await handleDelete();
  }, [stageId, currentStage, projectDetail?.description, confirm, handleDelete]);

  // Sync stage form data with currentStage data
  useEffect(() => {
    if (currentStage) {
      setStageFormData({
        stage_id: currentStage.stage_id || '',
        stage_description: currentStage.stage_description || '',
        po: currentStage.po || '',
        // RWC-specific fields
        region: currentStage.region || '',
        retaining_standard: currentStage.retaining_standard || '',
        startup_meeting: currentStage.startup_meeting || false,
        number_of_inspections: currentStage.number_of_inspections?.toString() || '',
        requires_qa: currentStage.requires_qa || false,
        // RWD-specific fields
        site_classification: currentStage.site_classification || '',
        // Contractor fields
        head_contractor_client_id: currentStage.head_contractor?.client?.id?.toString() || '',
        head_contractor_contact_id: currentStage.head_contractor?.contact?.id?.toString() || '',
        wall_contractor_client_id: currentStage.wall_contractor?.client?.id?.toString() || '',
        wall_contractor_contact_id: currentStage.wall_contractor?.contact?.id?.toString() || '',
        care_of: currentStage.care_of || '',
        // The stage's own contact only: '' means it inherits the project's.
        primary_contact: currentStage.primary_contact?.id?.toString() || ''
      });
    } else {
      setStageFormData({
        stage_id: '',
        stage_description: '',
        po: '',
        region: '',
        retaining_standard: '',
        startup_meeting: false,
        number_of_inspections: '',
        requires_qa: false,
        site_classification: '',
        head_contractor_client_id: '',
        head_contractor_contact_id: '',
        wall_contractor_client_id: '',
        wall_contractor_contact_id: '',
        care_of: '',
        primary_contact: ''
      });
    }
  }, [currentStage]);

  // The name of a stage contact chosen since the modal opened, from the
  // field's onChange, for the identity strip. A stage save does not refetch
  // projectDetail (that would re-sync stageFormData over fields still waiting
  // on their debounce), so currentStage keeps the contact the modal opened with.
  const [stageContactName, setStageContactName] = useState<string | null>(null);

  // Fetch workflow statuses for both projects and stages
  useEffect(() => {
    const fetchWorkflowStatuses = async () => {
      if (!projectDetail?.types) return;
      
      try {
        const { data, error } = await supabase
          .rpc('get_workflow_statuses_for_type', { p_job_type: projectDetail.types });
        
        if (error) {
          console.error('Error fetching workflow statuses:', error);
          return;
        }
        
        setWorkflowStatuses(data || []);
      } catch (err) {
        console.error('Error fetching workflow statuses:', err);
      }
    };

    fetchWorkflowStatuses();
  }, [projectDetail?.types, supabase]);

  // Get the current workflow status ID for stages, falling back to matching by status name
  const currentStageWorkflowStatusId = useMemo(() => {
    if (!currentStage || workflowStatuses.length === 0) return '';
    
    // Get the first workflow status ID as the default fallback
    const firstStatusId = String(workflowStatuses[0].id);
    
    // If workflow_status_id is set (not null/undefined), use it
    if (currentStage.workflow_status_id !== null && currentStage.workflow_status_id !== undefined) {
      const wsId = String(currentStage.workflow_status_id);
      // Verify this ID exists in our list
      if (workflowStatuses.some(ws => String(ws.id) === wsId)) {
        return wsId;
      }
    }
    
    // Fall back to finding workflow status by matching status name
    if (currentStage.status) {
      const statusToMatch = currentStage.status.toLowerCase().trim();
      const matchingStatus = workflowStatuses.find(
        ws => ws.status_name.toLowerCase().trim() === statusToMatch
      );
      
      // If found, return the matching status ID
      if (matchingStatus) {
        return String(matchingStatus.id);
      }
    }
    
    // Always return the first workflow status as default
    return firstStatusId;
  }, [currentStage, workflowStatuses]);

  /**
   * The status name to show, for the project and for the open stage: the
   * workflow status (what the boards and registers show), falling back to the
   * legacy `status` column only for a row that has no workflow status. The
   * legacy column is not kept in step with the board -- an RWC job in QA
   * Review still reads "Probable" there -- so it must not be what the modal
   * shows. Null while the board's statuses are still loading, rather than
   * flashing the legacy value first.
   */
  const projectStatusName = useMemo((): string | null => {
    if (!projectDetail) return null;
    if (projectDetail.workflow_status_id != null) {
      if (workflowStatuses.length === 0) return null;
      const ws = workflowStatuses.find(w => w.id === projectDetail.workflow_status_id);
      if (ws) return ws.status_name;
    }
    return projectDetail.status || null;
  }, [projectDetail, workflowStatuses]);

  const stageStatusName = useMemo((): string | null => {
    if (!currentStage) return null;
    return workflowStatuses.find(ws => String(ws.id) === currentStageWorkflowStatusId)?.status_name
      || currentStage.status
      || null;
  }, [currentStage, workflowStatuses, currentStageWorkflowStatusId]);

  /**
   * Handle stage form input changes
   */
  const handleStageInputChange = useCallback((field: keyof typeof stageFormData, value: string, isDropdown = false) => {
    setStageFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'head_contractor_client_id' && value !== prev.head_contractor_client_id) {
        updated.head_contractor_contact_id = '';
      }
      if (field === 'wall_contractor_client_id' && value !== prev.wall_contractor_client_id) {
        updated.wall_contractor_contact_id = '';
      }
      return updated;
    });

    if (isDropdown) {
      autoSaveStageField(field, value);
      if (field === 'head_contractor_client_id') {
        autoSaveStageField('head_contractor_contact_id', '');
      }
      if (field === 'wall_contractor_client_id') {
        autoSaveStageField('wall_contractor_contact_id', '');
      }
    } else {
      debouncedSaveStageField(field, value);
    }
  }, [autoSaveStageField, debouncedSaveStageField]);

  /**
   * Update workflow status for project or stage
   */
  const handleUpdateWorkflowStatus = useCallback(async (newStatusId: string, entityType: 'project' | 'stage' = 'stage') => {
    const itemId = entityType === 'stage' ? stageId : projectId;
    if (!itemId || !newStatusId) return;

    setSaveStatus('saving');
    try {
      const { data, error } = await supabase
        .rpc('update_workflow_status', {
          p_item_id: itemId,
          p_entity_type: entityType,
          p_new_status_id: parseInt(newStatusId, 10)
        });

      if (error) {
        throw new Error(error.message);
      }

      if (data && !data.success) {
        throw new Error(data.error || 'Failed to update workflow status');
      }

      // Refresh project detail silently to get updated data without blinking
      await fetchProjectDetail(true);
      showSavedStatus();
      onProjectUpdated();
    } catch (err) {
      console.error('Error updating workflow status:', err);
      setSaveStatus('error');
      setErrors({ general: err instanceof Error ? err.message : 'Failed to update workflow status' });
    }
  }, [stageId, projectId, supabase, fetchProjectDetail, showSavedStatus, onProjectUpdated]);

  // =======================================
  // INSPECTION HANDLERS
  // =======================================

  /**
   * Add a new inspection
   */
  /**
   * "SI 3". Never positional: a row's position in the list is a different number
   * from its SI number, and the two disagreeing is what this replaced.
   */
  const inspectionLabel = (inspection: Inspection): string =>
    inspection.sequence_number != null ? `SI ${inspection.sequence_number}` : '—';

  /**
   * Returns whether the inspection was actually recorded. The caller needs to
   * know: this reports failure by setting inspectionError rather than throwing,
   * so a void return let the modal close on a rejected save and the reason land
   * behind it.
   */
  const handleAddInspection = useCallback(async (): Promise<boolean> => {
    if (!inspectionFormData.inspection_date || !inspectionFormData.team_member_id || !inspectionFormData.description.trim()) {
      setInspectionError('All fields are required');
      return false;
    }

    setIsAddingInspection(true);
    setInspectionError(null);

    try {
      const params: {
        p_project_id?: number | null;
        p_stage_id?: number | null;
        p_inspection_date: string;
        p_team_member_id: string;
        p_description: string;
        p_sequence_number: number | null;
      } = {
        p_project_id: stageId ? null : projectId,
        p_stage_id: stageId || null,
        p_inspection_date: inspectionFormData.inspection_date,
        p_team_member_id: inspectionFormData.team_member_id,
        p_description: inspectionFormData.description.trim(),
        // Blank means "allocate the next one". Never computed here: a Draft site
        // instruction holds a number with no inspection row behind it, and may
        // not even be visible to this user.
        p_sequence_number: inspectionFormData.sequence_number.trim() === ''
          ? null
          : parseInt(inspectionFormData.sequence_number, 10)
      };

      const { data, error } = await supabase.rpc('add_inspection', params);

      if (error) {
        throw new Error(error.message);
      }

      if (data && !data.success) {
        throw new Error(data.error || 'Failed to add inspection');
      }

      // Reset form and refresh inspections
      setInspectionFormData({ sequence_number: '', inspection_date: '', team_member_id: '', description: '' });
      await fetchInspections();
      return true;
    } catch (err) {
      console.error('Error adding inspection:', err);
      setInspectionError(err instanceof Error ? err.message : 'Failed to add inspection');
      return false;
    } finally {
      setIsAddingInspection(false);
    }
  }, [inspectionFormData, projectId, stageId, supabase, fetchInspections]);

  /**
   * Start editing an inspection
   */
  const handleStartEditInspection = useCallback((inspection: Inspection) => {
    setEditingInspectionId(inspection.id);
    setEditInspectionFormData({
      sequence_number: inspection.sequence_number != null ? String(inspection.sequence_number) : '',
      inspection_date: inspection.inspection_date,
      team_member_id: inspection.team_member.id,
      description: inspection.description
    });
    setInspectionError(null);
  }, []);

  /**
   * Cancel editing an inspection
   */
  const handleCancelEditInspection = useCallback(() => {
    setEditingInspectionId(null);
    setEditInspectionFormData({ sequence_number: '', inspection_date: '', team_member_id: '', description: '' });
  }, []);

  /**
   * Save edited inspection
   */
  const handleSaveInspection = useCallback(async () => {
    if (!editingInspectionId) return;
    
    if (!editInspectionFormData.inspection_date || !editInspectionFormData.team_member_id || !editInspectionFormData.description.trim()) {
      setInspectionError('All fields are required');
      return;
    }

    setIsUpdatingInspection(true);
    setInspectionError(null);

    try {
      const { data, error } = await supabase.rpc('update_inspection', {
        p_inspection_id: editingInspectionId,
        p_inspection_date: editInspectionFormData.inspection_date,
        p_team_member_id: editInspectionFormData.team_member_id,
        p_description: editInspectionFormData.description.trim(),
        p_sequence_number: editInspectionFormData.sequence_number.trim() === ''
          ? null
          : parseInt(editInspectionFormData.sequence_number, 10)
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data && !data.success) {
        throw new Error(data.error || 'Failed to update inspection');
      }

      // Exit edit mode and refresh
      setEditingInspectionId(null);
      setEditInspectionFormData({ sequence_number: '', inspection_date: '', team_member_id: '', description: '' });
      await fetchInspections();
    } catch (err) {
      console.error('Error updating inspection:', err);
      setInspectionError(err instanceof Error ? err.message : 'Failed to update inspection');
    } finally {
      setIsUpdatingInspection(false);
    }
  }, [editingInspectionId, editInspectionFormData, supabase, fetchInspections]);

  /**
   * Delete an inspection
   */
  const handleDeleteInspection = useCallback(async (inspectionId: number) => {
    setIsDeletingInspection(inspectionId);
    setInspectionError(null);

    try {
      const { data, error } = await supabase.rpc('delete_inspection', {
        p_inspection_id: inspectionId
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data && !data.success) {
        throw new Error(data.error || 'Failed to delete inspection');
      }

      await fetchInspections();
    } catch (err) {
      console.error('Error deleting inspection:', err);
      setInspectionError(err instanceof Error ? err.message : 'Failed to delete inspection');
    } finally {
      setIsDeletingInspection(null);
    }
  }, [supabase, fetchInspections]);

  // =======================================
  // DRAWINGS (RWD)
  // =======================================

  const fetchDrawings = useCallback(async () => {
    if (!stageId) return;
    setIsLoadingDrawings(true);
    try {
      const { data, error } = await supabase
        .from('project_drawings')
        .select('*')
        .eq('stage_id', stageId)
        .order('sort_order');
      if (error) throw error;
      setDrawings(data || []);
    } catch (err) {
      console.error('Error fetching drawings:', err);
    } finally {
      setIsLoadingDrawings(false);
    }
  }, [stageId, supabase]);

  useEffect(() => {
    if (stageId && projectDetail?.types === 'RWD') {
      fetchDrawings();
    }
  }, [stageId, projectDetail?.types, fetchDrawings]);

  const handleAddDrawing = useCallback(async () => {
    if (!stageId || !drawingForm.drawing_number.trim() || !drawingForm.title.trim()) return;
    try {
      const { error } = await supabase
        .from('project_drawings')
        .insert({
          stage_id: stageId,
          drawing_number: drawingForm.drawing_number.trim(),
          issue_number: drawingForm.issue_number.trim() || '0',
          title: drawingForm.title.trim(),
          sort_order: drawings.length,
        });
      if (error) throw error;
      setDrawingForm({ drawing_number: '', issue_number: '0', title: '' });
      await fetchDrawings();
    } catch (err) {
      console.error('Error adding drawing:', err);
    }
  }, [stageId, drawingForm, drawings.length, supabase, fetchDrawings]);

  const handleUpdateDrawing = useCallback(async (id: number, updates: Partial<ProjectDrawing>) => {
    try {
      const { error } = await supabase
        .from('project_drawings')
        .update(updates)
        .eq('id', id);
      if (error) throw error;
      await fetchDrawings();
    } catch (err) {
      console.error('Error updating drawing:', err);
    }
  }, [supabase, fetchDrawings]);

  const handleDeleteDrawing = useCallback(async (id: number) => {
    try {
      const { error } = await supabase
        .from('project_drawings')
        .delete()
        .eq('id', id);
      if (error) throw error;
      await fetchDrawings();
    } catch (err) {
      console.error('Error deleting drawing:', err);
    }
  }, [supabase, fetchDrawings]);

  /**
   * Design standard is a multi-select.
   *
   * It used to clear every row on the stage before setting one, backed by a
   * partial UNIQUE index. A design certificate routinely cites more than one
   * drawing, so both are gone and this is an ordinary per-row toggle.
   */
  const handleToggleDesignStandard = useCallback(
    (drawing: ProjectDrawing) =>
      handleUpdateDrawing(drawing.id, { is_design_standard: !drawing.is_design_standard }),
    [handleUpdateDrawing]
  );

  const handleMoveDrawing = useCallback(async (index: number, direction: 'up' | 'down') => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === drawings.length - 1)) return;
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    const current = drawings[index];
    const swap = drawings[swapIndex];
    try {
      await supabase.from('project_drawings').update({ sort_order: swap.sort_order }).eq('id', current.id);
      await supabase.from('project_drawings').update({ sort_order: current.sort_order }).eq('id', swap.id);
      await fetchDrawings();
    } catch (err) {
      console.error('Error reordering drawings:', err);
    }
  }, [drawings, supabase, fetchDrawings]);

  // Check if construction tab should be shown (Design or RWC types)
  const showConstructionTab = useMemo(() => {
    return projectDetail?.types === 'Design' || projectDetail?.types === 'RWC';
  }, [projectDetail?.types]);

  // Header status indicator for auto-save
  const headerStatusContent = useMemo(() => {
    if (saveStatus === 'idle') return null;
    const labels = { saving: 'Saving…', saved: 'All changes saved', error: 'Save failed' } as const;
    return <SaveDot state={saveStatus} label={labels[saveStatus]} />;
  }, [saveStatus]);

  /**
   * Update a completion date (start_up_completion_date or qa_completion_date)
   */
  const handleUpdateCompletionDate = useCallback(async (
    field: 'start_up_completion_date' | 'qa_completion_date',
    value: string
  ) => {
    setSaveStatus('saving');
    setInspectionError(null);

    try {
      if (stageId) {
        // Update stage
        const { data, error } = await supabase.rpc('update_project_stage', {
          p_stage_id: stageId,
          p_stage_data: { [field]: value || null }
        });

        if (error) throw new Error(error.message);
        if (data && !data.success) throw new Error(data.error || 'Failed to update');
      } else if (projectId) {
        // Update project
        const { data, error } = await supabase.rpc('update_project_detail', {
          project_id: projectId,
          project_data: { [field]: value || null }
        });

        if (error) throw new Error(error.message);
        if (data && !data.success) throw new Error(data.error || 'Failed to update');
      }

      // Refresh to get updated data
      await fetchProjectDetail(true);
      showSavedStatus();
      onProjectUpdated();
    } catch (err) {
      console.error('Error updating completion date:', err);
      setSaveStatus('error');
      setInspectionError(err instanceof Error ? err.message : 'Failed to update completion date');
    }
  }, [stageId, projectId, supabase, fetchProjectDetail, showSavedStatus, onProjectUpdated]);

  /**
   * Insert or update a single PEP QA checklist item (required and/or completion date)
   * for the current project or stage.
   */
  const handleUpsertQaItem = useCallback(async (
    qaType: string,
    changes: { required?: boolean; completion_date?: string | null }
  ) => {
    setSaveStatus('saving');
    setInspectionError(null);

    // Merge with the existing item so we always send the full current state
    const existing = qaItems.find(item => item.qa_type === qaType);
    const required = changes.required !== undefined ? changes.required : (existing?.required ?? false);
    const completionDate = changes.completion_date !== undefined
      ? (changes.completion_date || null)
      : (existing?.completion_date ?? null);

    try {
      const { data, error } = await supabase.rpc('upsert_rwc_qa_item', {
        ...(stageId ? { p_stage_id: stageId } : { p_project_id: projectId }),
        p_qa_type: qaType,
        p_required: required,
        p_completion_date: completionDate
      });

      if (error) throw new Error(error.message);
      if (data && !data.success) throw new Error(data.error || 'Failed to save QA item');

      await fetchProjectDetail(true);
      showSavedStatus();
      onProjectUpdated();
    } catch (err) {
      console.error('Error saving QA item:', err);
      setSaveStatus('error');
      setInspectionError(err instanceof Error ? err.message : 'Failed to save QA item');
    }
  }, [qaItems, stageId, projectId, supabase, fetchProjectDetail, showSavedStatus, onProjectUpdated]);

  // Fetch inspections when construction tab is activated, or when viewing the PEP
  // (job/stage) tab of an RWC project so the read-only inspection mirror has data.
  useEffect(() => {
    const isRwc = projectDetail?.types === 'RWC';
    const onPepTab = activeTab === 'job' || activeTab === 'stage';
    const shouldFetch =
      (activeTab === 'construction' && showConstructionTab) ||
      (isRwc && onPepTab);
    if (shouldFetch && projectId) {
      fetchInspections();
      fetchActiveEngineers();
      void supabase
        .rpc('get_available_document_templates', { p_project_id: projectId, p_stage_id: stageId ?? null })
        .then(({ data, error }) => {
          if (error) { console.error('Error loading document templates:', error); return; }
          setInspectionTemplate(
            ((data as AvailableTemplate[]) || []).find(t => t.creates_inspection) ?? null
          );
        });
    }
  }, [activeTab, showConstructionTab, projectDetail?.types, projectId, stageId, supabase, fetchInspections, fetchActiveEngineers]);

  // Sync local completion date state with project/stage data
  useEffect(() => {
    if (stageId && currentStage) {
      setLocalStartUpDate(currentStage.start_up_completion_date || '');
      setLocalQaDate(currentStage.qa_completion_date || '');
    } else if (projectDetail) {
      setLocalStartUpDate(projectDetail.start_up_completion_date || '');
      setLocalQaDate(projectDetail.qa_completion_date || '');
    }
  }, [stageId, currentStage, projectDetail]);

  // Sync PEP QA items + their local date inputs with project/stage data
  useEffect(() => {
    const items = (stageId && currentStage ? currentStage.qa_items : projectDetail?.qa_items) || [];
    setQaItems(items);
    const dateMap: Record<string, string> = {};
    items.forEach(item => { dateMap[item.qa_type] = item.completion_date || ''; });
    setLocalQaItemDates(dateMap);
  }, [stageId, currentStage, projectDetail]);

  // Sync Design PEP lists (project-level) with project data
  useEffect(() => {
    setDrawingIndex(projectDetail?.drawing_index || []);
    setRisks(projectDetail?.risks || []);
    setProgrammeItems(projectDetail?.programme_items || []);
  }, [projectDetail]);

  // ---- Design PEP: Project Risk handlers ----
  const handleUpsertRisk = useCallback(async (
    presetKey: string,
    changes: { rating?: string | null; mitigation?: string | null }
  ) => {
    setSaveStatus('saving');
    const existing = risks.find(r => r.preset_key === presetKey);
    const rating = changes.rating !== undefined ? (changes.rating || null) : (existing?.rating ?? null);
    const mitigation = changes.mitigation !== undefined ? (changes.mitigation || null) : (existing?.mitigation ?? null);
    // Optimistic update so the rating dropdown doesn't flicker
    setRisks(prev => {
      if (existing) return prev.map(r => r.preset_key === presetKey ? { ...r, rating, mitigation } : r);
      return [...prev, { id: -Date.now(), preset_key: presetKey, risk_label: null, rating, mitigation, sort_order: 0 }];
    });
    try {
      const { data, error } = await supabase.rpc('upsert_project_risk', {
        p_project_id: projectId,
        p_preset_key: presetKey,
        p_rating: rating,
        p_mitigation: mitigation,
      });
      if (error) throw new Error(error.message);
      if (data && !data.success) throw new Error(data.error || 'Failed to save risk');
      await fetchProjectDetail(true);
      showSavedStatus();
      onProjectUpdated();
    } catch (err) {
      console.error('Error saving risk:', err);
      setSaveStatus('error');
      await fetchProjectDetail(true);
    }
  }, [risks, projectId, supabase, fetchProjectDetail, showSavedStatus, onProjectUpdated]);

  const handleAddCustomRisk = useCallback(async () => {
    if (!projectId || !customRiskLabel.trim()) return;
    setSaveStatus('saving');
    try {
      const { error } = await supabase
        .from('project_risks')
        .insert({ project_id: projectId, risk_label: customRiskLabel.trim(), sort_order: risks.length });
      if (error) throw error;
      setCustomRiskLabel('');
      await fetchProjectDetail(true);
      showSavedStatus();
      onProjectUpdated();
    } catch (err) {
      console.error('Error adding custom risk:', err);
      setSaveStatus('error');
    }
  }, [projectId, customRiskLabel, risks.length, supabase, fetchProjectDetail, showSavedStatus, onProjectUpdated]);

  const handleUpdateRisk = useCallback(async (id: number, updates: Partial<ProjectRisk>) => {
    setRisks(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
    try {
      const { error } = await supabase.from('project_risks').update(updates).eq('id', id);
      if (error) throw error;
    } catch (err) {
      console.error('Error updating risk:', err);
      await fetchProjectDetail(true);
    }
  }, [supabase, fetchProjectDetail]);

  const handleDeleteRisk = useCallback(async (id: number) => {
    try {
      const { error } = await supabase.from('project_risks').delete().eq('id', id);
      if (error) throw error;
      await fetchProjectDetail(true);
    } catch (err) {
      console.error('Error deleting risk:', err);
    }
  }, [supabase, fetchProjectDetail]);

  // ---- Design PEP: Programme handlers ----
  const handleUpsertProgramme = useCallback(async (
    phase: string,
    changes: { target_date?: string | null; actual_date?: string | null; notes?: string | null; removed?: boolean },
    discipline: 'civil' | 'structural' | null = null
  ) => {
    setSaveStatus('saving');
    const existing = programmeItems.find(p => p.phase === phase && normDiscipline(p.discipline) === normDiscipline(discipline));
    const target_date = changes.target_date !== undefined ? (changes.target_date || null) : (existing?.target_date ?? null);
    const actual_date = changes.actual_date !== undefined ? (changes.actual_date || null) : (existing?.actual_date ?? null);
    const notes = changes.notes !== undefined ? (changes.notes || null) : (existing?.notes ?? null);
    const removed = changes.removed !== undefined ? changes.removed : (existing?.removed ?? false);
    try {
      const { data, error } = await supabase.rpc('upsert_programme_item', {
        p_project_id: projectId,
        p_phase: phase,
        p_discipline: discipline,
        p_target_date: target_date,
        p_actual_date: actual_date,
        p_notes: notes,
        p_removed: removed,
      });
      if (error) throw new Error(error.message);
      if (data && !data.success) throw new Error(data.error || 'Failed to save programme item');
      await fetchProjectDetail(true);
      showSavedStatus();
      onProjectUpdated();
    } catch (err) {
      console.error('Error saving programme item:', err);
      setSaveStatus('error');
      await fetchProjectDetail(true);
    }
  }, [programmeItems, projectId, supabase, fetchProjectDetail, showSavedStatus, onProjectUpdated]);

  const handleAddCustomProgramme = useCallback(async () => {
    if (!projectId || !customProgrammeName.trim()) return;
    setSaveStatus('saving');
    try {
      const { error } = await supabase
        .from('project_programme_items')
        .insert({ project_id: projectId, custom_name: customProgrammeName.trim(), sort_order: 100 + programmeItems.length });
      if (error) throw error;
      setCustomProgrammeName('');
      await fetchProjectDetail(true);
      showSavedStatus();
      onProjectUpdated();
    } catch (err) {
      console.error('Error adding custom programme item:', err);
      setSaveStatus('error');
    }
  }, [projectId, customProgrammeName, programmeItems.length, supabase, fetchProjectDetail, showSavedStatus, onProjectUpdated]);

  const handleUpdateProgramme = useCallback(async (id: number, updates: Partial<ProgrammeItem>) => {
    setProgrammeItems(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
    try {
      const { error } = await supabase.from('project_programme_items').update(updates).eq('id', id);
      if (error) throw error;
    } catch (err) {
      console.error('Error updating programme item:', err);
      await fetchProjectDetail(true);
    }
  }, [supabase, fetchProjectDetail]);

  const handleDeleteProgramme = useCallback(async (id: number) => {
    try {
      const { error } = await supabase.from('project_programme_items').delete().eq('id', id);
      if (error) throw error;
      await fetchProjectDetail(true);
    } catch (err) {
      console.error('Error deleting programme item:', err);
    }
  }, [supabase, fetchProjectDetail]);

  // ---- Design PEP: Drawing Index handlers ----
  const handleAddDrawingIndex = useCallback(async (discipline: 'civil' | 'structural') => {
    if (!projectId) return;
    const count = drawingIndex.filter(d => d.discipline === discipline).length;
    try {
      const { error } = await supabase
        .from('project_drawing_index')
        .insert({ project_id: projectId, discipline, title: '', description: '', sort_order: count });
      if (error) throw error;
      await fetchProjectDetail(true);
      onProjectUpdated();
    } catch (err) {
      console.error('Error adding drawing index row:', err);
    }
  }, [projectId, drawingIndex, supabase, fetchProjectDetail, onProjectUpdated]);

  const handleUpdateDrawingIndex = useCallback(async (id: number, updates: Partial<DrawingIndexEntry>) => {
    try {
      const { error } = await supabase.from('project_drawing_index').update(updates).eq('id', id);
      if (error) throw error;
      await fetchProjectDetail(true);
    } catch (err) {
      console.error('Error updating drawing index row:', err);
    }
  }, [supabase, fetchProjectDetail]);

  const handleDeleteDrawingIndex = useCallback(async (id: number) => {
    try {
      const { error } = await supabase.from('project_drawing_index').delete().eq('id', id);
      if (error) throw error;
      await fetchProjectDetail(true);
    } catch (err) {
      console.error('Error deleting drawing index row:', err);
    }
  }, [supabase, fetchProjectDetail]);

  // =======================================
  // RENDER HELPERS
  // =======================================

  /**
   * Renders a contact dropdown with an add-contact popover button and info button
   */
  const renderContactField = (
    fieldKey: string,
    contactValue: string,
    clientIdValue: string,
    onContactChange: (contactId: string) => void,
    placeholder?: string
  ) => {
    const isAdding = addingContactFor === fieldKey;
    const refreshKey = contactRefreshKeys[fieldKey] || 0;

    return (
      <Field
        label="Contact"
        action={clientIdValue ? (
          <button
            type="button"
            className="panel-heading__action"
            onClick={() => {
              handleCancelAddContact();
              if (!isAdding) setAddingContactFor(fieldKey);
            }}
          >
            {isAdding ? 'Cancel' : '+ Add contact'}
          </button>
        ) : undefined}
      >
        <div className="contact-field">
          <SearchableContactDropdown
            key={refreshKey}
            value={contactValue || null}
            onChange={(contactId) => onContactChange(contactId?.toString() || '')}
            placeholder={clientIdValue ? (placeholder || 'Select contact...') : 'Select a company first'}
            allowNone={true}
            noneLabel="None"
            clientId={clientIdValue ? parseInt(clientIdValue, 10) : undefined}
            disabled={!clientIdValue}
          />
          <ContactInfoButton contactId={contactValue || null} />
        </div>
        {/* Inline, under the field, rather than a floating popover: a popover
            is clipped by the scrolling body when the field is near its edge. */}
        {isAdding && (
          <NewContactCard
            clientId={parseInt(clientIdValue, 10)}
            onCancel={handleCancelAddContact}
            onCreated={(contactId) => {
              // Through the caller's own setter, which knows the column name.
              // This used to take the field *key* and pick a setter from
              // `isStage`, but a stage's key carries a `stage_` prefix for the
              // form's identity -- so the stage branch sent
              // `stage_head_contractor_contact_id` to update_project_stage,
              // which builds its SQL from the keys it is given and failed on a
              // column that does not exist. The contact was created and then
              // not linked to anything.
              onContactChange(contactId);
              setContactRefreshKeys(prev => ({ ...prev, [fieldKey]: (prev[fieldKey] || 0) + 1 }));
              setAddingContactFor(null);
            }}
          />
        )}
      </Field>
    );
  };

  /**
   * The Head Contractor company + contact pair.
   *
   * Shared by the RWC PEP (where it has always lived) and the Construction
   * Admin tab, which is where a Design project edits it. Both write the same
   * two columns -- head_contractor_client_id / head_contractor_contact_id --
   * which exist on every project and stage regardless of job type, so there is
   * one value, not two.
   *
   * Advisory and RWD have no Construction Admin tab and so no field at all;
   * their site instructions leave Contractor blank for the engineer to type in
   * the document's Fields sidebar.
   */
  const renderHeadContractorSection = (isStage: boolean) => {
    const fd = isStage ? stageFormData : formData;
    const contactKeyPrefix = isStage ? 'stage_' : '';
    const onField = (field: string, value: string, immediate = false): void => {
      if (isStage) handleStageInputChange(field as keyof typeof stageFormData, value, immediate);
      else handleInputChange(field as keyof ProjectFormData, value, immediate);
    };
    const fs = (name: string) => fieldSaveState(isStage ? `stage:${name}` : `project:${name}`);

    return (
      <>
        <PanelHeading as="h5" spaced="sm" title="Head contractor" />
        <div className="pdm-grid pdm-grid--2">
          <Field label="Company" saveState={fs('head_contractor_client_id')}>
            <SearchableClientDropdown
              value={fd.head_contractor_client_id || null}
              onChange={(clientId) => onField('head_contractor_client_id', clientId?.toString() || '', true)}
              placeholder="Select company..."
              allowNone={true}
              noneLabel="None"
            />
          </Field>
          {renderContactField(
            `${contactKeyPrefix}head_contractor_contact_id`,
            fd.head_contractor_contact_id,
            fd.head_contractor_client_id,
            (id) => onField('head_contractor_contact_id', id, true)
          )}
        </div>
      </>
    );
  };

  /**
   * The RWC PEP: startup meeting, planned inspections, QA, and the two
   * contractors. Shared by the project and stage views, which differ only in
   * which form state and save path they use.
   */
  const renderRwcPep = (isStage: boolean) => {
    const fd = isStage ? stageFormData : formData;
    const contactKeyPrefix = isStage ? 'stage_' : '';
    const source = isStage ? currentStage : projectDetail;
    const fs = (name: string) => fieldSaveState(isStage ? `stage:${name}` : `project:${name}`);

    // Typed wrappers so the project/stage branch is resolved once (avoids a
    // union-of-setters that TypeScript can't call).
    const onField = (field: string, value: string, immediate = false): void => {
      if (isStage) handleStageInputChange(field as keyof typeof stageFormData, value, immediate);
      else handleInputChange(field as keyof ProjectFormData, value, immediate);
    };
    const toggleField = (field: 'startup_meeting' | 'requires_qa', checked: boolean): void => {
      if (isStage) {
        setStageFormData(prev => ({ ...prev, [field]: checked }));
        autoSaveStageField(field, checked);
      } else {
        setFormData(prev => ({ ...prev, [field]: checked }));
        autoSaveProjectField(field, checked);
      }
    };

    // Read-only inspection mirror: planned rows 1..N, completion dates pulled from
    // the actual inspection records (ascending by date). Editing stays in Construction Admin.
    const numInspections = parseInt(fd.number_of_inspections as string, 10) || 0;
    // Ascending by SI number -- the register's own order, and the same number
    // Construction Admin shows. Date is only the tiebreak for a row that somehow
    // has none.
    const actualAsc = [...inspections].sort((a, b) => {
      const an = a.sequence_number ?? Number.MAX_SAFE_INTEGER;
      const bn = b.sequence_number ?? Number.MAX_SAFE_INTEGER;
      return an !== bn ? an - bn : a.inspection_date.localeCompare(b.inspection_date);
    });
    // Never fewer rows than there are records: a job with more inspections than
    // it planned used to hide the extras behind the planned count.
    const inspectionRowCount = Math.max(numInspections, actualAsc.length);

    return (
      <>
        <PanelHeading
          id="pep-rwc"
          spaced
          title="RWC PEP"
          hint="Retaining wall construction jobs track the startup meeting, planned inspections and QA here. The inspections themselves are recorded in Construction Admin."
        />
        <div className="pdm-grid pdm-grid--3">
          <Field label="Region" saveState={fs('region')}>
            <Dropdown
              items={[
                { id: '', label: 'None', value: '' },
                ...RWC_REGION_OPTIONS.map(option => ({ id: option, label: option, value: option }))
              ]}
              value={fd.region}
              onChange={(value) => onField('region', value, true)}
              placeholder="Select region..."
              searchable={false}
            />
          </Field>
          <Field label="Retaining standard" saveState={fs('retaining_standard')}>
            <Dropdown
              items={[
                { id: '', label: 'None', value: '' },
                ...RETAINING_STANDARD_OPTIONS.map(option => ({ id: option, label: option, value: option }))
              ]}
              value={fd.retaining_standard}
              onChange={(value) => onField('retaining_standard', value, true)}
              placeholder="Select standard..."
              searchable={false}
            />
          </Field>
        </div>

        {/* Startup meeting */}
        <PanelHeading as="h5" spaced="sm" title="Startup meeting" meta={<SaveDot state={fs('startup_meeting')} />} />
        <div className="ui-row">
          <Toggle
            checked={fd.startup_meeting}
            onChange={(checked) => toggleField('startup_meeting', checked)}
            label="Startup meeting required"
          />
          {fd.startup_meeting && (
            <>
              <span className="ui-row__spacer" />
              <label className="ui-row__inline">
                <span className="ui-row__label">Held on</span>
                <TextInput
                  type="date"
                  size="sm"
                  className="is-date"
                  value={localStartUpDate}
                  onChange={(e) => setLocalStartUpDate(e.target.value)}
                  onBlur={(e) => {
                    const currentValue = source?.start_up_completion_date || '';
                    if (e.target.value !== currentValue) {
                      handleUpdateCompletionDate('start_up_completion_date', e.target.value);
                    }
                  }}
                  disabled={saveStatus === 'saving'}
                />
              </label>
            </>
          )}
        </div>

        {/* Inspections (read-only mirror of Construction Admin records) */}
        <PanelHeading
          as="h5"
          spaced="sm"
          title="Inspections"
          meta={inspectionRowCount > 0 ? `${actualAsc.length} of ${numInspections} recorded` : undefined}
        />
        <div className="ui-row">
          <label className="ui-row__inline">
            <span className="ui-row__label">Planned inspections</span>
            <TextInput
              type="number"
              min={0}
              size="sm"
              mono
              className="is-narrow"
              value={fd.number_of_inspections}
              onChange={(e) => onField('number_of_inspections', e.target.value)}
              placeholder="0"
            />
          </label>
          <SaveDot state={fs('number_of_inspections')} />
        </div>
        {inspectionRowCount > 0 ? (
          <table className="ui-table">
            <thead>
              <tr>
                <th>SI No</th>
                <th className="col-date">Completion date</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: inspectionRowCount }, (_, i) => {
                const insp = actualAsc[i];
                return (
                  <tr key={i}>
                    {/* A planned slot with nothing in it yet has no number:
                        one is only allocated when the inspection exists. */}
                    <td className="ui-table__mono">{insp ? `SI ${insp.sequence_number ?? ''}`.trim() : '—'}</td>
                    <td>
                      {insp ? (
                        new Date(insp.inspection_date).toLocaleDateString('en-AU')
                      ) : (
                        <span className="ui-table__pending">Pending</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="ui-table__empty">
            Set the number of planned inspections to track them here. Inspections are recorded in the Construction Admin tab.
          </p>
        )}

        {/* QA */}
        <PanelHeading as="h5" spaced="sm" title="QA" meta={<SaveDot state={fs('requires_qa')} />} />
        <div className="ui-row">
          <Toggle
            checked={fd.requires_qa}
            onChange={(checked) => toggleField('requires_qa', checked)}
            label="Requires QA"
          />
          {fd.requires_qa && (
            <>
              <span className="ui-row__spacer" />
              <label className="ui-row__inline">
                <span className="ui-row__label">QA certificate date</span>
                <TextInput
                  type="date"
                  size="sm"
                  className="is-date"
                  value={localQaDate}
                  onChange={(e) => setLocalQaDate(e.target.value)}
                  onBlur={(e) => {
                    const currentValue = source?.qa_completion_date || '';
                    if (e.target.value !== currentValue) {
                      handleUpdateCompletionDate('qa_completion_date', e.target.value);
                    }
                  }}
                  disabled={saveStatus === 'saving'}
                />
              </label>
            </>
          )}
        </div>
        {fd.requires_qa && (
          <table className="ui-table">
            <thead>
              <tr>
                <th>QA item</th>
                <th className="col-toggle is-center">Required</th>
                <th className="col-date">Completion date</th>
              </tr>
            </thead>
            <tbody>
              {RWC_QA_ITEMS.map(qa => {
                const item = qaItems.find(q => q.qa_type === qa.qa_type);
                const required = item?.required ?? false;
                return (
                  <tr key={qa.qa_type}>
                    <td>{qa.label}</td>
                    <td className="is-center">
                      <Toggle
                        size="sm"
                        checked={required}
                        onChange={(checked) => handleUpsertQaItem(qa.qa_type, { required: checked })}
                        aria-label={`${qa.label} required`}
                      />
                    </td>
                    <td>
                      {required ? (
                        <TextInput
                          type="date"
                          size="sm"
                          className="is-date"
                          value={localQaItemDates[qa.qa_type] || ''}
                          onChange={(e) => setLocalQaItemDates(prev => ({ ...prev, [qa.qa_type]: e.target.value }))}
                          onBlur={(e) => {
                            const currentValue = item?.completion_date || '';
                            if (e.target.value !== currentValue) {
                              handleUpsertQaItem(qa.qa_type, { completion_date: e.target.value });
                            }
                          }}
                          disabled={saveStatus === 'saving'}
                          aria-label={`${qa.label} completion date`}
                        />
                      ) : (
                        <span className="ui-table__na">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Contractors */}
        {renderHeadContractorSection(isStage)}

        <PanelHeading as="h5" spaced="sm" title="Wall contractor" />
        <div className="pdm-grid pdm-grid--2">
          <Field label="Company" saveState={fs('wall_contractor_client_id')}>
            <SearchableClientDropdown
              value={fd.wall_contractor_client_id || null}
              onChange={(clientId) => onField('wall_contractor_client_id', clientId?.toString() || '', true)}
              placeholder="Select company..."
              allowNone={true}
              noneLabel="None"
            />
          </Field>
          {renderContactField(
            `${contactKeyPrefix}wall_contractor_contact_id`,
            fd.wall_contractor_contact_id,
            fd.wall_contractor_client_id,
            (id) => onField('wall_contractor_contact_id', id, true)
          )}
        </div>
      </>
    );
  };

  /**
   * One discipline's drawing index: the S (structural) or C (civil) series.
   * Rows edit in place; the inputs are uncontrolled and save on blur.
   */
  const renderDrawingIndexTable = (discipline: 'civil' | 'structural', label: string) => {
    const prefix = discipline === 'civil' ? 'C' : 'S';
    const rows = drawingIndex
      .filter(d => d.discipline === discipline)
      .sort((a, b) => a.sort_order - b.sort_order);
    return (
      <div className="pdm-block">
        <h6 className="pdm-sublabel">{label}</h6>
        {rows.length > 0 && (
          <table className="ui-table">
            <thead>
              <tr>
                <th className="col-narrow">Series</th>
                <th>Title</th>
                <th>Description / contents</th>
                <th className="ui-table__actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const editing = editingDrawingIndexId === row.id;
                const series = `${prefix}${idx}`;
                return (
                  <tr key={row.id} className={editing ? 'is-editing' : ''}>
                    <td className="ui-table__mono">{series}</td>
                    {editing ? (
                      <>
                        <td>
                          <TextInput
                            size="sm"
                            defaultValue={row.title || ''}
                            onBlur={(e) => { if (e.target.value !== (row.title || '')) handleUpdateDrawingIndex(row.id, { title: e.target.value }); }}
                            aria-label={`${series} title`}
                            autoFocus
                          />
                        </td>
                        <td>
                          <TextInput
                            size="sm"
                            defaultValue={row.description || ''}
                            onBlur={(e) => { if (e.target.value !== (row.description || '')) handleUpdateDrawingIndex(row.id, { description: e.target.value }); }}
                            aria-label={`${series} description`}
                          />
                        </td>
                        <td className="ui-table__actions">
                          <span className="row-actions">
                            <IconButton size="sm" tone="accent" icon={<CheckIcon />} label="Done" onClick={() => setEditingDrawingIndexId(null)} />
                            <IconButton
                              size="sm"
                              tone="danger"
                              icon={<TrashIcon />}
                              label={`Delete ${series}`}
                              onClick={async () => {
                                const ok = await confirm({
                                  title: `Delete series ${series}?`,
                                  message: <>The series and its description will be removed from the drawing index.</>,
                                  confirmLabel: 'Delete',
                                  tone: 'danger',
                                });
                                if (ok) handleDeleteDrawingIndex(row.id);
                              }}
                            />
                          </span>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{row.title || <span className="ui-table__na">—</span>}</td>
                        <td>{row.description || <span className="ui-table__na">—</span>}</td>
                        <td className="ui-table__actions">
                          <span className="row-actions">
                            <IconButton size="sm" icon={<EditIcon />} label={`Edit ${series}`} onClick={() => setEditingDrawingIndexId(row.id)} />
                          </span>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div className="chip-bar">
          <button type="button" className="panel-heading__action" onClick={() => handleAddDrawingIndex(discipline)}>
            <PlusIcon size={12} /> Add {prefix} series
          </button>
        </div>
      </div>
    );
  };

  /**
   * The Design PEP (project-level): Programme, Scope of Works, Exclusions,
   * Project Risk, then the Drawing Index (structural always, civil when
   * has_civil_scope) last.
   */
  const renderDesignPep = () => {
    // Programme: present standard phases (not removed) + custom rows; removed phases go to the add-bar.
    // Discipline split mirrors the fee plan — null == structural (legacy/single).
    const programmeFor = (phase: string, discipline: 'civil' | 'structural' | null) =>
      programmeItems.find(p => p.phase === phase && normDiscipline(p.discipline) === normDiscipline(discipline));
    const customProgramme = programmeItems.filter(p => !p.phase).sort((a, b) => a.sort_order - b.sort_order);
    const customRisks = risks.filter(r => !r.preset_key).sort((a, b) => a.sort_order - b.sort_order);
    const riskRatingOptions = RISK_RATING_OPTIONS.map(o => ({ value: o, label: o }));

    // Render one discipline's programme: standard phases (present rows + add-bar
    // for removed ones). Custom rows + the custom-add input render once, under
    // the structural table (`showCustom`). When civil scope is off, `discipline`
    // is null so rows keep today's single-discipline behaviour.
    const renderProgrammeTable = (discipline: 'civil' | 'structural' | null, showHeading: boolean, showCustom: boolean) => {
      const presentStandard = FEE_PHASES.filter(phase => { const r = programmeFor(phase, discipline); return !(r && r.removed); });
      const removedStandard = FEE_PHASES.filter(phase => { const r = programmeFor(phase, discipline); return r && r.removed; });
      return (
        <div className="pdm-block" key={discipline ?? 'structural'}>
          {showHeading && <h6 className="pdm-sublabel">{discipline === 'civil' ? 'Civil' : 'Structural'}</h6>}
          <table className="ui-table">
            <thead>
              <tr>
                <th>Stage / deliverable</th>
                <th className="col-date">Target date</th>
                <th className="col-date">Actual date</th>
                <th>Notes</th>
                <th className="ui-table__actions" />
              </tr>
            </thead>
            <tbody>
              {presentStandard.map(phase => {
                const row = programmeFor(phase, discipline);
                return (
                  <tr key={phase}>
                    <td>{phase}</td>
                    <td>
                      <TextInput type="date" size="sm" defaultValue={row?.target_date || ''} aria-label={`${phase} target date`}
                        onBlur={(e) => { if (e.target.value !== (row?.target_date || '')) handleUpsertProgramme(phase, { target_date: e.target.value }, discipline); }} />
                    </td>
                    <td>
                      <TextInput type="date" size="sm" defaultValue={row?.actual_date || ''} aria-label={`${phase} actual date`}
                        onBlur={(e) => { if (e.target.value !== (row?.actual_date || '')) handleUpsertProgramme(phase, { actual_date: e.target.value }, discipline); }} />
                    </td>
                    <td>
                      <TextInput size="sm" defaultValue={row?.notes || ''} aria-label={`${phase} notes`}
                        onBlur={(e) => { if (e.target.value !== (row?.notes || '')) handleUpsertProgramme(phase, { notes: e.target.value }, discipline); }} />
                    </td>
                    <td className="ui-table__actions">
                      <span className="row-actions">
                        <IconButton size="sm" icon={<CloseIcon />} label={`Remove ${phase}`} onClick={() => handleUpsertProgramme(phase, { removed: true }, discipline)} />
                      </span>
                    </td>
                  </tr>
                );
              })}
              {showCustom && customProgramme.map(row => (
                <tr key={row.id}>
                  <td>
                    <TextInput size="sm" defaultValue={row.custom_name || ''} aria-label="Custom stage name"
                      onBlur={(e) => { if (e.target.value !== (row.custom_name || '')) handleUpdateProgramme(row.id, { custom_name: e.target.value }); }} />
                  </td>
                  <td>
                    <TextInput type="date" size="sm" defaultValue={row.target_date || ''} aria-label="Target date"
                      onBlur={(e) => { if (e.target.value !== (row.target_date || '')) handleUpdateProgramme(row.id, { target_date: e.target.value || null }); }} />
                  </td>
                  <td>
                    <TextInput type="date" size="sm" defaultValue={row.actual_date || ''} aria-label="Actual date"
                      onBlur={(e) => { if (e.target.value !== (row.actual_date || '')) handleUpdateProgramme(row.id, { actual_date: e.target.value || null }); }} />
                  </td>
                  <td>
                    <TextInput size="sm" defaultValue={row.notes || ''} aria-label="Notes"
                      onBlur={(e) => { if (e.target.value !== (row.notes || '')) handleUpdateProgramme(row.id, { notes: e.target.value || null }); }} />
                  </td>
                  <td className="ui-table__actions">
                    <span className="row-actions">
                      <IconButton size="sm" tone="danger" icon={<TrashIcon />} label="Delete stage" onClick={() => handleDeleteProgramme(row.id)} />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {removedStandard.length > 0 && (
            <div className="chip-bar">
              <span className="chip-bar__label">Add back</span>
              {removedStandard.map(phase => (
                <button key={phase} type="button" className="panel-heading__action" onClick={() => handleUpsertProgramme(phase, { removed: false }, discipline)}>
                  <PlusIcon size={12} /> {phase}
                </button>
              ))}
            </div>
          )}
        </div>
      );
    };

    return (
      <>
        {/* Programme */}
        <PanelHeading
          id="pep-programme"
          spaced
          title="Programme"
          hint="Target and actual dates for each phase. Remove a phase that does not apply; it can be added back from the bar under the table."
        />
        {formData.has_civil_scope ? (
          <>
            {renderProgrammeTable('structural', true, true)}
            {renderProgrammeTable('civil', true, false)}
          </>
        ) : (
          renderProgrammeTable(null, false, true)
        )}
        <div className="ui-table__add">
          <TextInput
            size="sm"
            placeholder="Add a custom stage…"
            aria-label="New custom stage"
            value={customProgrammeName}
            onChange={(e) => setCustomProgrammeName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustomProgramme(); } }}
          />
          <button type="button" className="pill-btn pill-btn--sm" onClick={handleAddCustomProgramme} disabled={!customProgrammeName.trim()}>
            <PlusIcon size={12} /> Add stage
          </button>
        </div>

        {/* Scope of Works */}
        <PanelHeading id="pep-scope" spaced title="Scope of works" meta={<SaveDot state={fieldSaveState('project:scope_of_works')} />} />
        <TextArea
          className="pdm-textarea"
          autoGrow
          rows={4}
          value={formData.scope_of_works}
          onChange={(e) => handleInputChange('scope_of_works', e.target.value)}
          placeholder="Project description and scope…"
          aria-label="Scope of works"
        />

        {/* Exclusions -- part of the same block as Scope of works, so a
            subsection gap rather than a ruled section break. */}
        <PanelHeading spaced="sm" title="Exclusions" meta={<SaveDot state={fieldSaveState('project:exclusions')} />} />
        <TextArea
          className="pdm-textarea"
          autoGrow
          rows={3}
          value={formData.exclusions}
          onChange={(e) => handleInputChange('exclusions', e.target.value)}
          placeholder="What is explicitly out of scope…"
          aria-label="Exclusions"
        />

        {/* Project Risk */}
        <PanelHeading
          id="pep-risk"
          spaced
          title="Project risk"
          hint="Rate each standard risk and note how it is handled. Add anything specific to this job below."
        />
        <table className="ui-table">
          <thead>
            <tr>
              <th>Risk</th>
              <th className="col-rating">Rating</th>
              <th>Mitigation / notes</th>
              <th className="ui-table__actions" />
            </tr>
          </thead>
          <tbody>
            {DESIGN_RISK_ITEMS.map(preset => {
              const row = risks.find(r => r.preset_key === preset.preset_key);
              return (
                <tr key={preset.preset_key}>
                  <td>{preset.label}</td>
                  <td className="col-rating">
                    <SegmentedControl
                      size="sm"
                      allowEmpty
                      aria-label={`${preset.label} rating`}
                      options={riskRatingOptions}
                      value={row?.rating || null}
                      onChange={(value) => handleUpsertRisk(preset.preset_key, { rating: value ?? '' })}
                    />
                  </td>
                  <td>
                    <TextInput
                      size="sm"
                      defaultValue={row?.mitigation || ''}
                      aria-label={`${preset.label} mitigation`}
                      onBlur={(e) => { if (e.target.value !== (row?.mitigation || '')) handleUpsertRisk(preset.preset_key, { mitigation: e.target.value }); }}
                    />
                  </td>
                  <td className="ui-table__actions" />
                </tr>
              );
            })}
            {customRisks.map(row => (
              <tr key={row.id}>
                <td>
                  <TextInput
                    size="sm"
                    defaultValue={row.risk_label || ''}
                    aria-label="Risk"
                    onBlur={(e) => { if (e.target.value !== (row.risk_label || '')) handleUpdateRisk(row.id, { risk_label: e.target.value }); }}
                  />
                </td>
                <td className="col-rating">
                  <SegmentedControl
                    size="sm"
                    allowEmpty
                    aria-label="Rating"
                    options={riskRatingOptions}
                    value={row.rating || null}
                    onChange={(value) => handleUpdateRisk(row.id, { rating: value })}
                  />
                </td>
                <td>
                  <TextInput
                    size="sm"
                    defaultValue={row.mitigation || ''}
                    aria-label="Mitigation"
                    onBlur={(e) => { if (e.target.value !== (row.mitigation || '')) handleUpdateRisk(row.id, { mitigation: e.target.value || null }); }}
                  />
                </td>
                <td className="ui-table__actions">
                  <span className="row-actions">
                    <IconButton size="sm" tone="danger" icon={<TrashIcon />} label="Delete risk" onClick={() => handleDeleteRisk(row.id)} />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="ui-table__add">
          <TextInput
            size="sm"
            placeholder="Add a risk specific to this job…"
            aria-label="New risk"
            value={customRiskLabel}
            onChange={(e) => setCustomRiskLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustomRisk(); } }}
          />
          <button type="button" className="pill-btn pill-btn--sm" onClick={handleAddCustomRisk} disabled={!customRiskLabel.trim()}>
            <PlusIcon size={12} /> Add risk
          </button>
        </div>

        {/* Drawing Index (Design only — not shown for Advisory) */}
        {formData.types === 'Design' && (
          <>
            <PanelHeading
              spaced
              title="Drawing index"
              meta={`${drawingIndex.length} series`}
              hint={formData.has_civil_scope
                ? 'Structural series numbered S0, S1…; civil series numbered C0, C1…'
                : 'Structural series numbered S0, S1… Turn on civil scope in Design details to add a Civil index.'}
            />
            {renderDrawingIndexTable('structural', 'Structural')}
            {formData.has_civil_scope && renderDrawingIndexTable('civil', 'Civil')}
          </>
        )}
      </>
    );
  };

  /**
   * What the PEP still needs, grouped the way the tab is. Computed from state
   * the modal already holds, so it updates as fields are filled in. Only the
   * groups that apply to the job type (and to the view: project or stage)
   * are listed; each row is a jump link to its section.
   */
  type PepItem = { label: string; done: boolean };
  type PepGroup = { id: string; label: string; items: PepItem[] };

  const pepGroups = (): PepGroup[] => {
    if (!projectDetail) return [];
    const type = projectDetail.types;
    const filled = (v: unknown) => v !== null && v !== undefined && String(v).trim() !== '';

    if (stageId && currentStage) {
      const groups: PepGroup[] = [{
        id: 'pep-stage',
        label: 'Stage details',
        items: [
          { label: 'stage ID', done: filled(stageFormData.stage_id) },
          { label: 'description', done: filled(stageFormData.stage_description) },
        ],
      }];
      if (type === 'RWC') {
        groups.push({
          id: 'pep-rwc',
          label: 'RWC PEP',
          items: [
            { label: 'region', done: filled(stageFormData.region) },
            { label: 'retaining standard', done: filled(stageFormData.retaining_standard) },
            { label: 'planned inspections', done: filled(stageFormData.number_of_inspections) },
            { label: 'head contractor', done: filled(stageFormData.head_contractor_client_id) },
          ],
        });
      }
      if (type === 'RWD') {
        groups.push({
          id: 'pep-rwd',
          label: 'RWD PEP',
          items: [
            { label: 'site classification', done: filled(stageFormData.site_classification) },
            { label: 'drawing set', done: drawings.length > 0 },
          ],
        });
      }
      return groups;
    }

    const designish = type === 'Design' || type === 'Advisory';
    const groups: PepGroup[] = [{
      id: 'pep-general',
      label: 'General',
      items: [
        { label: 'client', done: !!projectDetail.client },
        { label: 'primary contact', done: !!projectDetail.primary_contact },
        { label: 'lead', done: !!projectDetail.project_lead },
        { label: 'description', done: filled(formData.description) },
        { label: 'site address', done: filled(formData.street_address) || filled(siteAddressDisplay) },
      ],
    }];
    if (designish) {
      groups.push({
        id: 'pep-design',
        label: 'Design details',
        items: [
          { label: 'structure type', done: formData.structure_type.length > 0 },
          { label: 'standard', done: filled(formData.standard) },
          { label: 'wall type', done: formData.wall_type.length > 0 },
          { label: 'roof type', done: formData.roof_type.length > 0 },
          { label: 'floor type', done: formData.floor_type.length > 0 },
        ],
      });
    }
    if (type !== 'RWC') {
      groups.push({
        id: 'pep-site',
        label: 'Site',
        items: [
          { label: 'wind region', done: filled(formData.wind_region) },
          { label: 'site classification', done: filled(formData.site_classification) },
        ],
      });
    }
    if (designish) {
      // A target date on every phase still in the programme, per discipline.
      const disciplines: Array<'civil' | 'structural' | null> = formData.has_civil_scope ? ['structural', 'civil'] : [null];
      const phases: PepItem[] = [];
      disciplines.forEach(discipline => {
        FEE_PHASES.forEach(phase => {
          const row = programmeItems.find(p => p.phase === phase && normDiscipline(p.discipline) === normDiscipline(discipline));
          if (row?.removed) return;
          phases.push({ label: discipline === 'civil' ? `${phase} (civil)` : phase, done: filled(row?.target_date) });
        });
      });
      groups.push({ id: 'pep-programme', label: 'Programme', items: phases });
      groups.push({
        id: 'pep-scope',
        label: 'Scope',
        items: [
          { label: 'scope of works', done: filled(formData.scope_of_works) },
          { label: 'exclusions', done: filled(formData.exclusions) },
        ],
      });
      groups.push({
        id: 'pep-risk',
        label: 'Project risk',
        items: DESIGN_RISK_ITEMS.map(preset => ({
          label: preset.label,
          done: filled(risks.find(r => r.preset_key === preset.preset_key)?.rating),
        })),
      });
    }
    if (type === 'RWC') {
      groups.push({
        id: 'pep-rwc',
        label: 'RWC PEP',
        items: [
          { label: 'region', done: filled(formData.region) },
          { label: 'retaining standard', done: filled(formData.retaining_standard) },
          { label: 'planned inspections', done: filled(formData.number_of_inspections) },
          { label: 'head contractor', done: filled(formData.head_contractor_client_id) },
        ],
      });
    }
    return groups;
  };

  /** Scroll a PEP section's heading to the top of the modal body. */
  const jumpTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /** "wind region, site classification" -- or a count when the list is long. */
  const missingText = (group: PepGroup): string => {
    const missing = group.items.filter(i => !i.done).map(i => i.label);
    if (missing.length === 0) return 'Complete';
    if (group.id === 'pep-programme') {
      return `${missing.length} of ${group.items.length} target dates`;
    }
    if (group.id === 'pep-risk') {
      return `${missing.length} of ${group.items.length} risks unrated`;
    }
    if (missing.length > 3) return `${missing.slice(0, 3).join(', ')} +${missing.length - 3} more`;
    return missing.join(', ');
  };

  /**
   * The card at the top of a PEP tab: who the job is for and who is on it,
   * its type and status, and what the PEP still needs. Read-only. The number
   * and description are in the title bar, so they are not repeated here.
   */
  const renderIdentityStrip = () => {
    if (!projectDetail) return null;
    const stageView = !!stageId && !!currentStage;
    const statusName = stageView ? stageStatusName : projectStatusName;
    const leadId = stageView
      ? (currentStage!.project_lead?.id ?? projectDetail.project_lead?.id)
      : projectDetail.project_lead?.id;
    const type = projectDetail.types as ProjectJobType;
    // A stage's own contact, else the project's it inherits. The stage's
    // name comes from currentStage while the field still holds the contact
    // the modal opened with, and from the field's own onChange once it moves.
    const ownContactId = stageView ? stageFormData.primary_contact : '';
    const ownContactName = !ownContactId
      ? null
      : currentStage?.primary_contact && String(currentStage.primary_contact.id) === ownContactId
        ? currentStage.primary_contact.full_name
        : stageContactName;
    const headerContactName = ownContactId ? ownContactName : projectDetail.primary_contact?.full_name;
    const team = (stageView ? currentStage!.team_members : projectDetail.team_members) ?? [];
    // The lead is always on the team, so they are shown once, as a tagged chip.
    const isLead = (m: { id: string }) => leadId != null && String(m.id) === String(leadId);
    const teamSorted = [...team].sort((a, b) => Number(isLead(b)) - Number(isLead(a)));
    // The checklist lives on the tab whose sections it points at.
    const showChecklist = stageView ? activeTab === 'stage' : activeTab === 'job';
    const groups = showChecklist ? pepGroups() : [];
    const allDone = groups.length > 0 && groups.every(g => g.items.every(i => i.done));

    return (
      <div className="identity-strip">
        <div className="identity-strip__head">
          <div className="identity-strip__main">
            <span className="identity-strip__who">
              <strong>{projectDetail.client?.company_name || <span className="is-empty">No client</span>}</strong>
              {/* A stage's own contact, else the project's it inherits. */}
              {headerContactName && (
                <>
                  <span className="sep">·</span>
                  <span>{headerContactName}</span>
                </>
              )}
            </span>
            <span className="identity-strip__team">
              {leadId == null && <span className="is-empty">No lead</span>}
              {teamSorted.length === 0 ? (
                <span className="is-empty">No team members yet</span>
              ) : (
                <>
                  {/* Initials, not names: a job with a handful of people ran
                      the row out of the strip and pushed the client name to an
                      ellipsis. The name is the tooltip and the label. */}
                  {teamSorted.slice(0, 8).map(m => (
                    <span
                      key={m.id}
                      className={`ui-avatar ${isLead(m) ? 'ui-avatar--lead' : ''}`.trim()}
                      role="img"
                      aria-label={isLead(m) ? `${m.full_name} (project lead)` : m.full_name}
                      title={isLead(m) ? `${m.full_name} · Lead` : m.full_name}
                    >
                      {initialsOf(m.full_name)}
                    </span>
                  ))}
                  {teamSorted.length > 8 && (
                    <span
                      className="ui-avatar ui-avatar--more"
                      title={teamSorted.slice(8).map(m => m.full_name).join(', ')}
                    >
                      +{teamSorted.length - 8}
                    </span>
                  )}
                </>
              )}
            </span>
          </div>
          <div className="identity-strip__side">
            <span className="identity-strip__type" title={JOB_TYPE_HINTS[type]}>{type}</span>
            {statusName && <WorkflowStatusPill status={statusName} />}
          </div>
        </div>

        {showChecklist && (allDone ? (
          <p className="pep-progress__done">
            <CheckIcon size={14} /> All PEP information has been entered.
          </p>
        ) : (
          <div className="pep-progress" role="list" aria-label="PEP completeness">
            {groups.map(group => {
              const done = group.items.filter(i => i.done).length;
              const state = done === group.items.length ? 'is-done' : done === 0 ? 'is-empty' : 'is-partial';
              return (
                <button
                  key={group.id}
                  type="button"
                  role="listitem"
                  className={`pep-progress__row ${state}`}
                  onClick={() => jumpTo(group.id)}
                  title={`Go to ${group.label}`}
                >
                  <span className="pep-progress__mark" aria-hidden="true">
                    {state === 'is-done' && <CheckIcon size={10} />}
                  </span>
                  <span className="pep-progress__label">{group.label}</span>
                  <span className="pep-progress__count">{done}/{group.items.length}</span>
                  <span className="pep-progress__missing">{missingText(group)}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  /** One row of a read-only field list. */
  const renderFact = (label: string, value: React.ReactNode, mono = false) => (
    <div className="field-list__row">
      <dt className="field-list__label">{label}</dt>
      <dd className={`field-list__value ${mono ? 'is-mono' : ''}`.trim()}>
        {value === null || value === undefined || value === '' ? <span className="is-empty">—</span> : value}
      </dd>
    </div>
  );

  if (!isOpen) return null;

  // Render form field
  /**
   * A Design details multi-select. Saves are debounced per field like a text
   * field's: each click sends the whole array, so saving on every click could
   * let an earlier selection land after a later one and win.
   */
  const renderMultiSelect = (
    field: MultiSelectField,
    options: readonly string[],
    placeholder: string,
    searchable = false
  ) => (
    <Dropdown
      items={options.map(option => ({ id: option, label: option, value: option }))}
      value={formData[field]}
      onChange={(values: string[]) => {
        setFormData(prev => ({ ...prev, [field]: values }));
        debouncedSaveProjectField(field, values.length > 0 ? values : null);
      }}
      placeholder={placeholder}
      searchable={searchable}
      multiSelect
    />
  );

  const renderFormField = (fieldName: string, config: ModalFieldConfig) => {
    if (config.type === 'select' && config.options) {
      return (
        <Dropdown
          items={[
            { id: '', label: 'None', value: '' },
            ...config.options.map(option => ({
              id: option,
              label: option,
              value: option
            }))
          ]}
          value={formData[fieldName as keyof ProjectFormData] as string}
          onChange={(value) => handleInputChange(fieldName as keyof ProjectFormData, value, true)}
          placeholder={`Select ${config.label.toLowerCase()}...`}
          searchable={false}
        />
      );
    }

    if (fieldName === 'project_lead') {
      return (
        <Dropdown
          items={teamMemberDropdownItems}
          value={formData.project_lead}
          onChange={(value) => handleInputChange('project_lead', value, true)}
          placeholder="Select project lead..."
          searchable={true}
        />
      );
    }

    if (fieldName === 'client') {
      return (
        <SearchableClientDropdown
          value={formData.client || null}
          onChange={(clientId) => handleInputChange('client', clientId?.toString() || '', true)}
          placeholder="Select client..."
          allowNone={true}
          noneLabel="None"
        />
      );
    }

    return (
      <TextInput
        id={`pdm-${fieldName}`}
        value={formData[fieldName as keyof ProjectFormData] as string || ''}
        onChange={(e) => handleInputChange(fieldName as keyof ProjectFormData, e.target.value)}
        invalid={!!errors[fieldName]}
        placeholder={`Enter ${config.label.toLowerCase()}...`}
      />
    );
  };

  // Build dynamic title from project data
  // If stageInfo is provided, use stage-formatted title: "<job_number> - [<stage_description>] <parent_description>"
  const modalTitle = stageInfo ? (
    <span className="pdm-title">
      <span className="pdm-title__number">{stageInfo.jobNumber}</span>
      <span className="pdm-title__text">[{stageInfo.stageDescription}] {stageInfo.parentDescription}</span>
    </span>
  ) : projectDetail ? (
    <span className="pdm-title">
      <span className="pdm-title__number">{projectDetail.job_number}</span>
      <span className="pdm-title__text">{projectDetail.description}</span>
    </span>
  ) : (
    'Loading...'
  );


  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={modalTitle}
      size="wide"
      hasTabs={true}
      noPadding={true}
      hasFooter={false}
      scrollable={true}
      headerStatus={headerStatusContent}
    >
      <TabbedModalLayout
        tabs={[
          { id: 'stage', label: 'Stage PEP', icon: <FlagIcon size={14} />, hidden: !stageId },
          { id: 'job', label: 'Project PEP', icon: <ClipboardIcon size={14} /> },
          { id: 'construction', label: 'Construction Admin', icon: <ToolIcon size={14} />, hidden: !showConstructionTab },
          {
            id: 'stages',
            label: 'Stages',
            icon: <LayersIcon size={14} />,
            hidden: !!stageId || projectDetail?.types === 'RWC',
          },
          { id: 'team', label: 'Team Members', icon: <UsersIcon size={14} /> },
          { id: 'documents', label: 'Documents', icon: <FileTextIcon size={14} /> },
          { id: 'settings', label: 'Settings', icon: <SlidersIcon size={14} /> },
        ] as TabDef[]}
        activeTabId={activeTab}
        onTabChange={(id) => { flushPendingSaves(); setActiveTab(id as typeof activeTab); }}
        sidebar={
          <ThreadPanel
            projectId={projectId}
            stageId={stageId || null}
            onMessageChange={onProjectUpdated}
          />
        }
      >
        <div className="project-detail-modal form-ui">
          {isLoading ? (
            <div className="loading-message">Loading project details...</div>
          ) : (
            <form onSubmit={(e) => e.preventDefault()} className="project-detail-form">
              {errors.general && (
                <div className="error-message general-error">{errors.general}</div>
              )}

              {activeTab === 'job' && (
                <div className="job-info-tab">
                  {renderIdentityStrip()}
                  {stageId ? (
                    <>
                      {/* Viewing a stage: the project's own facts, read-only. */}
                      <PanelHeading title="General" hint="The project this stage belongs to. Open the project itself to change these." />
                      <dl className="field-list">
                        {renderFact('Client', projectDetail?.client?.company_name)}
                        {/* The project's own; the stage's is in Stage details. */}
                        {renderFact('Project contact', projectDetail?.primary_contact?.full_name)}
                        {renderFact('Type', projectDetail?.types)}
                        {renderFact('Status', projectStatusName ? <WorkflowStatusPill status={projectStatusName} /> : null)}
                        {renderFact(
                          'Site address',
                          projectDetail?.street_address
                            ? [
                                projectDetail.street_address,
                                [projectDetail.city, projectDetail.state, projectDetail.postcode].filter(Boolean).join(' '),
                                projectDetail.country !== 'Australia' ? projectDetail.country : null
                              ].filter(Boolean).join(', ')
                            : null
                        )}
                        {renderFact('Description', projectDetail?.description)}
                        {renderFact(
                          'Tags',
                          projectTags.length > 0
                            ? projectTags.map(tag => <span key={tag.id} className="ui-chip">{tag.name}</span>)
                            : null
                        )}
                        {projectDetail?.types !== 'RWD' && renderFact('PO', projectDetail?.po, true)}
                      </dl>

                      {(projectDetail?.types === 'Design' || projectDetail?.types === 'Advisory') && (
                        <>
                          <PanelHeading spaced title="Design details" />
                          <dl className="field-list">
                            {renderFact('Structure type', projectDetail?.structure_type?.length ? projectDetail.structure_type.join(', ') : null)}
                            {renderFact(
                              'NCC class',
                              projectDetail?.structure_type?.length
                                ? nccClassesFor(projectDetail.structure_type).map(c => <span key={c} className="ui-chip">Class {c}</span>)
                                : null
                            )}
                            {renderFact('Standard', projectDetail?.standard)}
                            {renderFact('Wall type', projectDetail?.wall_type?.length ? projectDetail.wall_type.join(', ') : null)}
                            {renderFact('Roof type', projectDetail?.roof_type?.length ? projectDetail.roof_type.join(', ') : null)}
                            {renderFact('Floor type', projectDetail?.floor_type?.length ? projectDetail.floor_type.join(', ') : null)}
                            {projectDetail?.types === 'Design' && renderFact('Civil scope', projectDetail?.has_civil_scope ? 'Yes' : 'No')}
                          </dl>
                        </>
                      )}

                      {projectDetail?.types !== 'RWC' && (
                        <>
                          <PanelHeading spaced title="Site" />
                          <dl className="field-list">
                            {renderFact('Wind region', projectDetail?.wind_region)}
                            {renderFact('Site classification', projectDetail?.site_classification ? `Class ${projectDetail.site_classification}` : null)}
                          </dl>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Viewing a project: everything edits in place. */}
                      <PanelHeading id="pep-general" title="General" />
                      {projectId && (
                        <GeneralInfoPanel
                          projectId={projectId}
                          stageId={null}
                          onChange={() => {
                            fetchProjectDetail(true);
                            onProjectUpdated();
                          }}
                        />
                      )}

                      <div className="pdm-grid pdm-grid--1 pdm-grid--after-panel">
                        <Field
                          label="Description"
                          required
                          htmlFor="pdm-description"
                          className="span-all"
                          saveState={fieldSaveState('project:description')}
                          error={errors.description}
                        >
                          {renderFormField('description', projectModalFields.description)}
                        </Field>
                        <Field
                          label="Site address"
                          className="span-all"
                          saveState={fieldSaveState('project:site_address')}
                          action={
                            <IconButton
                              size="sm"
                              icon={<ChevronDownIcon />}
                              label={showAddressDetails ? 'Hide address fields' : 'Show address fields'}
                              active={showAddressDetails}
                              onClick={() => setShowAddressDetails(prev => !prev)}
                            />
                          }
                        >
                          <AddressAutocomplete
                            onPlaceSelected={handleProjectAddressSelected}
                            value={siteAddressDisplay}
                            onChange={(val) => setSiteAddressDisplay(val)}
                            placeholder="Search address..."
                          />
                        </Field>
                      </div>
                      {showAddressDetails && (
                        <div className="pdm-grid pdm-grid--4">
                          <Field label="Street" htmlFor="pdm-street" className="span-2" saveState={fieldSaveState('project:street_address')}>
                            <TextInput id="pdm-street" value={formData.street_address} onChange={(e) => handleInputChange('street_address', e.target.value)} placeholder="Street address" />
                          </Field>
                          <Field label="City / suburb" htmlFor="pdm-city" saveState={fieldSaveState('project:city')}>
                            <TextInput id="pdm-city" value={formData.city} onChange={(e) => handleInputChange('city', e.target.value)} placeholder="City" />
                          </Field>
                          <Field label="State" htmlFor="pdm-state" saveState={fieldSaveState('project:state')}>
                            <TextInput id="pdm-state" value={formData.state} onChange={(e) => handleInputChange('state', e.target.value)} placeholder="State" />
                          </Field>
                          <Field label="Postcode" htmlFor="pdm-postcode" saveState={fieldSaveState('project:postcode')}>
                            <TextInput id="pdm-postcode" mono value={formData.postcode} onChange={(e) => handleInputChange('postcode', e.target.value)} placeholder="Postcode" />
                          </Field>
                          <Field label="Country" htmlFor="pdm-country" saveState={fieldSaveState('project:country')}>
                            <TextInput id="pdm-country" value={formData.country} onChange={(e) => handleInputChange('country', e.target.value)} placeholder="Country" />
                          </Field>
                        </div>
                      )}
                      <div className="pdm-grid pdm-grid--2">
                        <Field label="Tags" saveState={fieldSaveState('project:tags')}>
                          <TagSelector
                            value={projectTags}
                            onChange={(newTags) => {
                              setProjectTags(newTags);
                              autoSaveProjectTags(newTags);
                            }}
                            placeholder="Search or add tags..."
                          />
                        </Field>
                        {/* PO field - hidden for RWD */}
                        {formData.types !== 'RWD' && (
                          <Field label="PO" htmlFor="pdm-po" saveState={fieldSaveState('project:po')}>
                            <TextInput
                              id="pdm-po"
                              mono
                              value={formData.po}
                              onChange={(e) => handleInputChange('po', e.target.value)}
                              placeholder="Purchase order number"
                            />
                          </Field>
                        )}
                      </div>

                      {/* Section: Design details (Design and Advisory) */}
                      {(formData.types === 'Design' || formData.types === 'Advisory') && (
                        <>
                          <PanelHeading
                            id="pep-design"
                            spaced
                            title="Design details"
                            hint={formData.types === 'Design'
                              ? 'What the building is, its drafting standard, wall, roof and floor construction, and whether the job carries a civil scope.'
                              : 'What the building is, its drafting standard, and its wall, roof and floor construction.'}
                          />
                          <div className="pdm-grid pdm-grid--4">
                            <Field label="Structure type" className="span-2" saveState={fieldSaveState('project:structure_type')}>
                              {renderMultiSelect('structure_type', PROJECT_STRUCTURE_TYPE_OPTIONS, 'Select structure types...', true)}
                            </Field>
                            <Field label="NCC class" className="span-2" hint="Set by the structure type.">
                              <div className="pdm-derived">
                                {formData.structure_type.length > 0
                                  ? nccClassesFor(formData.structure_type).map(c => <span key={c} className="ui-chip">Class {c}</span>)
                                  : <span className="pdm-derived__empty">None yet</span>}
                              </div>
                            </Field>
                            <Field label="Standard" saveState={fieldSaveState('project:standard')}>
                              {renderFormField('standard', projectModalFields.standard)}
                            </Field>
                            <Field label="Wall type" saveState={fieldSaveState('project:wall_type')}>
                              {renderMultiSelect('wall_type', PROJECT_WALL_TYPE_OPTIONS, 'Select wall types...')}
                            </Field>
                            <Field label="Roof type" saveState={fieldSaveState('project:roof_type')}>
                              {renderMultiSelect('roof_type', PROJECT_ROOF_TYPE_OPTIONS, 'Select roof types...')}
                            </Field>
                            <Field label="Floor type" saveState={fieldSaveState('project:floor_type')}>
                              {renderMultiSelect('floor_type', PROJECT_FLOOR_TYPE_OPTIONS, 'Select floor types...')}
                            </Field>
                            {formData.types === 'Design' && (
                              <Field
                                label="Civil scope"
                                className="span-2"
                                saveState={fieldSaveState('project:has_civil_scope')}
                                hint="Adds a Civil drawing index and a Civil programme to the PEP."
                              >
                                <Toggle
                                  checked={formData.has_civil_scope}
                                  onChange={(checked) => {
                                    setFormData(prev => ({ ...prev, has_civil_scope: checked }));
                                    autoSaveProjectField('has_civil_scope', checked);
                                  }}
                                  label="This job has a civil scope"
                                />
                              </Field>
                            )}
                          </div>
                        </>
                      )}

                      {/* Section: Site (not shown for RWC) */}
                      {formData.types !== 'RWC' && (
                        <>
                          <PanelHeading id="pep-site" spaced title="Site" />
                          <div className="pdm-grid pdm-grid--4">
                            <Field label="Wind region" saveState={fieldSaveState('project:wind_region')}>
                              {renderFormField('wind_region', projectModalFields.wind_region)}
                            </Field>
                            <Field label="Site classification" saveState={fieldSaveState('project:site_classification')}>
                              {renderFormField('site_classification', projectModalFields.site_classification)}
                            </Field>
                          </div>
                        </>
                      )}

                      {formData.types === 'RWC' && renderRwcPep(false)}

                      {(formData.types === 'Design' || formData.types === 'Advisory') && renderDesignPep()}
                    </>
                  )}
                </div>
              )}

              {/* Stage Information Tab - only shown when viewing a stage */}
              {activeTab === 'stage' && stageId && currentStage && (
                <div className="stage-info-tab">
                  {errors.stage && (
                    <div className="error-message general-error">{errors.stage}</div>
                  )}
                  {renderIdentityStrip()}

                  <PanelHeading id="pep-stage" title="Stage details" />
                  <div className="pdm-grid pdm-grid--3">
                    <Field
                      label="Stage ID"
                      required
                      htmlFor="pdm-stage-id"
                      saveState={fieldSaveState('stage:stage_id')}
                      hint={`Numbered ${projectDetail?.job_number ?? ''}.${stageFormData.stage_id || '…'}`}
                    >
                      <TextInput
                        id="pdm-stage-id"
                        mono
                        value={stageFormData.stage_id}
                        onChange={(e) => handleStageInputChange('stage_id', e.target.value)}
                        placeholder="1"
                      />
                    </Field>
                    <Field label="Status">
                      {workflowStatuses.length === 0 ? (
                        <div className="general-info-panel__readonly">{currentStage?.status || 'Loading…'}</div>
                      ) : (
                        <Dropdown
                          key={`stage-status-${projectDetail?.types}-${workflowStatuses.length}`}
                          // Exempts the saved status, not currentStageWorkflowStatusId,
                          // which falls back to the legacy column or the first status.
                          items={workflowStatusDropdownItems(
                            workflowStatuses, projectDetail?.types, canUseManagerOnlyStatuses(userRole),
                            currentStage?.workflow_status_id,
                          )}
                          value={currentStageWorkflowStatusId}
                          onChange={(value) => handleUpdateWorkflowStatus(value, 'stage')}
                          placeholder="Select status..."
                          searchable={false}
                          disabled={saveStatus === 'saving'}
                        />
                      )}
                    </Field>
                    {/* PO field - hidden for RWD */}
                    {projectDetail?.types !== 'RWD' && (
                      <Field label="PO" htmlFor="pdm-stage-po" saveState={fieldSaveState('stage:po')}>
                        <TextInput
                          id="pdm-stage-po"
                          mono
                          value={stageFormData.po}
                          onChange={(e) => handleStageInputChange('po', e.target.value)}
                          placeholder="Purchase order number"
                        />
                      </Field>
                    )}
                    <Field
                      label="Description"
                      required
                      htmlFor="pdm-stage-description"
                      className="span-all"
                      saveState={fieldSaveState('stage:stage_description')}
                    >
                      <TextInput
                        id="pdm-stage-description"
                        value={stageFormData.stage_description}
                        onChange={(e) => handleStageInputChange('stage_description', e.target.value)}
                        placeholder="e.g. Stage 17, Civil"
                      />
                    </Field>
                    {/* The stage's own primary contact, from the project's
                        client. Left empty, the stage uses the project's. */}
                    <StageContactField
                      className="span-all"
                      value={stageFormData.primary_contact}
                      onChange={(contactId, fullName) => {
                        setStageContactName(fullName);
                        handleStageInputChange('primary_contact', contactId, true);
                      }}
                      clientId={formData.client}
                      projectContactName={projectDetail?.primary_contact?.full_name ?? null}
                      saveState={fieldSaveState('stage:primary_contact')}
                    />
                  </div>

                  {/* PEP section - only shown for RWC type */}
                  {projectDetail?.types === 'RWC' && renderRwcPep(true)}

                  {/* RWD Details section - only shown for RWD type */}
                  {projectDetail?.types === 'RWD' && (
                    <>
                      <PanelHeading
                        id="pep-rwd"
                        spaced
                        title="RWD PEP"
                        hint="Site classification and the drawing set for this stage."
                      />
                      <div className="pdm-grid pdm-grid--3">
                        <Field label="Site classification" saveState={fieldSaveState('stage:site_classification')}>
                          <Dropdown
                            items={[
                              { id: '', label: 'None', value: '' },
                              ...PROJECT_SITE_CLASSIFICATION_OPTIONS.map(option => ({
                                id: option,
                                label: `Class ${option}`,
                                value: option
                              }))
                            ]}
                            value={stageFormData.site_classification}
                            onChange={(value) => handleStageInputChange('site_classification', value, true)}
                            placeholder="Select site class..."
                            searchable={false}
                          />
                        </Field>
                      </div>

                      {/* Drawing Set */}
                      <PanelHeading
                        as="h5"
                        spaced="sm"
                        title="Drawing set"
                        meta={drawings.length > 0 ? `${drawings.length} ${drawings.length === 1 ? 'drawing' : 'drawings'}` : undefined}
                      />
                      {isLoadingDrawings ? (
                        <p className="ui-table__empty">Loading drawings…</p>
                      ) : (
                        <>
                          {drawings.length > 0 && (
                            <table className="ui-table">
                              <thead>
                                <tr>
                                  <th className="col-code">Drawing</th>
                                  <th className="col-narrow">Issue</th>
                                  <th>Title</th>
                                  {/* Row actions sit before the switch so the switch
                                      lands on the table's right edge; they are only
                                      visible on hover, so the gap reads as empty. */}
                                  <th className="ui-table__actions" />
                                  <th className="col-toggle is-center">Design std</th>
                                </tr>
                              </thead>
                              <tbody>
                                {drawings.map((drawing, idx) => {
                                  const editing = editingDrawingId === drawing.id;
                                  return (
                                    <tr key={drawing.id} className={editing ? 'is-editing' : ''}>
                                      {editing ? (
                                        <>
                                          <td>
                                            <TextInput
                                              size="sm"
                                              mono
                                              defaultValue={drawing.drawing_number}
                                              aria-label="Drawing number"
                                              autoFocus
                                              onBlur={(e) => {
                                                if (e.target.value !== drawing.drawing_number) {
                                                  handleUpdateDrawing(drawing.id, { drawing_number: e.target.value });
                                                }
                                              }}
                                            />
                                          </td>
                                          <td>
                                            <TextInput
                                              size="sm"
                                              defaultValue={drawing.issue_number}
                                              aria-label="Issue"
                                              onBlur={(e) => {
                                                if (e.target.value !== drawing.issue_number) {
                                                  handleUpdateDrawing(drawing.id, { issue_number: e.target.value });
                                                }
                                              }}
                                            />
                                          </td>
                                          <td>
                                            <TextInput
                                              size="sm"
                                              defaultValue={drawing.title}
                                              aria-label="Title"
                                              onBlur={(e) => {
                                                if (e.target.value !== drawing.title) {
                                                  handleUpdateDrawing(drawing.id, { title: e.target.value });
                                                }
                                              }}
                                            />
                                          </td>
                                        </>
                                      ) : (
                                        <>
                                          <td className="ui-table__mono col-code">{drawing.drawing_number}</td>
                                          <td>Issue {drawing.issue_number}</td>
                                          <td>{drawing.title}</td>
                                        </>
                                      )}
                                      <td className="ui-table__actions">
                                        <span className="row-actions">
                                          {editing ? (
                                            <>
                                              <IconButton size="sm" tone="accent" icon={<CheckIcon />} label="Done" onClick={() => setEditingDrawingId(null)} />
                                              <IconButton
                                                size="sm"
                                                tone="danger"
                                                icon={<TrashIcon />}
                                                label={`Delete ${drawing.drawing_number}`}
                                                onClick={async () => {
                                                  const ok = await confirm({
                                                    title: `Delete drawing ${drawing.drawing_number}?`,
                                                    message: <>{drawing.title || 'This drawing'} will be removed from the set.</>,
                                                    confirmLabel: 'Delete',
                                                    tone: 'danger',
                                                  });
                                                  if (ok) handleDeleteDrawing(drawing.id);
                                                }}
                                              />
                                            </>
                                          ) : (
                                            <>
                                              <IconButton size="sm" icon={<EditIcon />} label={`Edit ${drawing.drawing_number}`} onClick={() => setEditingDrawingId(drawing.id)} />
                                              <IconButton size="sm" icon={<ArrowUpIcon />} label="Move up" onClick={() => handleMoveDrawing(idx, 'up')} disabled={idx === 0} />
                                              <IconButton size="sm" icon={<ArrowDownIcon />} label="Move down" onClick={() => handleMoveDrawing(idx, 'down')} disabled={idx === drawings.length - 1} />
                                            </>
                                          )}
                                        </span>
                                      </td>
                                      <td className="is-center">
                                        {/* A switch, not a radio: a stage can have several
                                            design standards. */}
                                        <Toggle
                                          size="sm"
                                          checked={drawing.is_design_standard}
                                          onChange={() => handleToggleDesignStandard(drawing)}
                                          aria-label={`${drawing.drawing_number} is a design standard`}
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}

                          {/* Add drawing form */}
                          <div className="ui-table__add">
                            <TextInput
                              size="sm"
                              mono
                              className="is-code"
                              placeholder="Drawing no."
                              aria-label="New drawing number"
                              value={drawingForm.drawing_number}
                              onChange={(e) => setDrawingForm(prev => ({ ...prev, drawing_number: e.target.value }))}
                            />
                            <TextInput
                              size="sm"
                              className="is-narrow"
                              placeholder="Issue"
                              aria-label="New drawing issue"
                              value={drawingForm.issue_number}
                              onChange={(e) => setDrawingForm(prev => ({ ...prev, issue_number: e.target.value }))}
                            />
                            <TextInput
                              size="sm"
                              placeholder="Title"
                              aria-label="New drawing title"
                              value={drawingForm.title}
                              onChange={(e) => setDrawingForm(prev => ({ ...prev, title: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDrawing(); } }}
                            />
                            <button
                              type="button"
                              className="pill-btn pill-btn--sm"
                              onClick={handleAddDrawing}
                              disabled={!drawingForm.drawing_number.trim() || !drawingForm.title.trim()}
                            >
                              <PlusIcon size={12} /> Add drawing
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {activeTab === 'stages' && projectDetail && (
                <StagesTab
                  projectId={projectDetail.id}
                  projectType={projectDetail.types}
                  teamMembers={teamMembers}
                  clientId={projectDetail.client?.id ?? null}
                  projectContactName={projectDetail.primary_contact?.full_name ?? null}
                  onStageCreated={() => { fetchProjectDetail(true); onProjectUpdated(); }}
                  onStageUpdated={() => { fetchProjectDetail(true); onProjectUpdated(); }}
                  onStageDeleted={() => { fetchProjectDetail(true); onProjectUpdated(); }}
                />
              )}

              {activeTab === 'team' && projectId && (
                <TeamPanel
                  projectId={projectId}
                  stageId={stageId ?? null}
                  onChange={() => {
                    fetchProjectDetail(true);
                    onProjectUpdated();
                  }}
                />
              )}

              {/* Documents Tab - project or stage scoped, grouped by category.
                  key on stageId so switching project<->stage re-fetches. */}
              {activeTab === 'documents' && projectId && (
                <DocumentsTab
                  key={stageId ?? 'project'}
                  projectId={projectId}
                  stageId={stageId ?? null}
                  isReadOnly={isReadOnly}
                  onClose={handleClose}
                  reopenIdentity={reopenIdentity}
                />
              )}

              {/* Construction Tab - Inspections tracking for Design and RWC */}
              {activeTab === 'construction' && showConstructionTab && projectDetail && (
                <div className="construction-tab">
                  {/* Design projects edit their head contractor here. RWC edits
                      the same two columns in its PEP, so it is not repeated. */}
                  {projectDetail.types === 'Design' && renderHeadContractorSection(!!stageId)}

                  {/* Raising a site instruction is how an inspection is
                      recorded now, so it leads. The paper/email form behind it
                      is for the handover cases and is deliberately one click
                      further away. Both are hidden for a read-only viewer rather
                      than one hidden and one greyed out, which read as a toolbar
                      that had failed to load. */}
                  <PanelHeading
                    spaced={projectDetail.types === 'Design'}
                    title="Inspections"
                    meta={isLoadingInspections ? undefined : `${inspections.length} recorded`}
                    hint={stageId
                      ? 'Inspections carried out for this stage. Raise a site inspection record to record one; each takes the next SI number.'
                      : 'Inspections carried out for this project. Raise a site inspection record to record one; each takes the next SI number.'}
                    action={!isReadOnly && inspectionTemplate ? (
                      <button
                        type="button"
                        className="pill-btn pill-btn--primary pill-btn--sm"
                        onClick={() => startCreateDocument(inspectionTemplate)}
                      >
                        <PlusIcon size={12} /> New
                      </button>
                    ) : undefined}
                  />

                  {inspectionError && (
                    <p className="field__error" role="alert">{inspectionError}</p>
                  )}

                  {isLoadingInspections ? (
                    <p className="ui-table__empty">Loading inspections…</p>
                  ) : inspections.length === 0 ? (
                    <p className="ui-table__empty">No inspections recorded yet.</p>
                  ) : (
                    <div className="ui-cards">
                      {inspections.map((inspection) => {
                        const editing = editingInspectionId === inspection.id;
                        const busy = isDeletingInspection === inspection.id;
                        return (
                          <div key={inspection.id} className={`ui-card ${editing ? 'is-editing' : ''}`}>
                            {editing ? (
                              <div className="pdm-grid pdm-grid--4">
                                <Field label="SI No" htmlFor={`insp-${inspection.id}-si`}>
                                  <TextInput
                                    id={`insp-${inspection.id}-si`}
                                    type="number"
                                    min={1}
                                    mono
                                    value={editInspectionFormData.sequence_number}
                                    onChange={(e) => setEditInspectionFormData(prev => ({ ...prev, sequence_number: e.target.value }))}
                                  />
                                </Field>
                                <Field label="Date" htmlFor={`insp-${inspection.id}-date`}>
                                  <TextInput
                                    id={`insp-${inspection.id}-date`}
                                    type="date"
                                    value={editInspectionFormData.inspection_date}
                                    onChange={(e) => setEditInspectionFormData(prev => ({ ...prev, inspection_date: e.target.value }))}
                                  />
                                </Field>
                                <Field label="Engineer" className="span-2">
                                  <Dropdown
                                    items={activeEngineers.map(eng => ({
                                      id: eng.id,
                                      label: eng.full_name,
                                      value: eng.id
                                    }))}
                                    value={editInspectionFormData.team_member_id}
                                    onChange={(value) => setEditInspectionFormData(prev => ({ ...prev, team_member_id: value }))}
                                    placeholder="Select engineer..."
                                    searchable={true}
                                  />
                                </Field>
                                {/* One line, matching the form: this is the
                                    inspection's name, not a report. */}
                                <Field label="Description" htmlFor={`insp-${inspection.id}-desc`} className="span-all">
                                  <TextInput
                                    id={`insp-${inspection.id}-desc`}
                                    value={editInspectionFormData.description}
                                    onChange={(e) => setEditInspectionFormData(prev => ({ ...prev, description: e.target.value }))}
                                  />
                                </Field>
                                <div className="pill-row pill-row--end span-all">
                                  <button type="button" className="pill-btn pill-btn--ghost pill-btn--sm" onClick={handleCancelEditInspection} disabled={isUpdatingInspection}>
                                    Cancel
                                  </button>
                                  <button type="button" className="pill-btn pill-btn--primary pill-btn--sm" onClick={handleSaveInspection} disabled={isUpdatingInspection}>
                                    {isUpdatingInspection ? 'Saving…' : 'Save inspection'}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="ui-card__main">
                                  <div className="ui-card__top">
                                    {/* A row written by an issued site instruction carries that
                                        document's number, which is stable. A hand-added row has
                                        no number of its own and is still labelled positionally. */}
                                    <span className="ui-card__number">{inspectionLabel(inspection)}</span>
                                    <span className="ui-card__title">{inspection.description}</span>
                                  </div>
                                  <div className="ui-card__meta">
                                    <span>
                                      {new Date(inspection.inspection_date).toLocaleDateString('en-AU', {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric'
                                      })}
                                    </span>
                                    <span>{inspection.team_member.full_name}</span>
                                    {inspection.document_id && <span className="ui-chip">Site inspection record</span>}
                                  </div>
                                </div>
                                <div className="ui-card__side">
                                  {inspection.document_id ? (
                                    // The document owns this record: it was written when the
                                    // site instruction was issued and is withdrawn if it is
                                    // reopened. Editing it here would put the two out of step,
                                    // so the edit happens in the document (update_inspection
                                    // and delete_inspection refuse these rows too).
                                    //
                                    // Ownership is tested on document_id, a plain column that
                                    // always comes back -- not on `document`, which is the
                                    // RLS-filtered join and is null for a viewer outside the
                                    // template's can_view. Testing that one offered them Edit
                                    // and Delete buttons the RPC would then refuse.
                                    <button
                                      type="button"
                                      className="pill-btn pill-btn--sm"
                                      disabled={!inspection.document}
                                      title={inspection.document ? undefined : 'You do not have access to this site inspection record'}
                                      onClick={() => {
                                        if (!inspection.document) return;
                                        // Same close-then-navigate the Documents
                                        // tab uses, so a return trip lands back
                                        // on this tab.
                                        openDocumentFromModal(inspection.document.id);
                                      }}
                                    >
                                      Open SI
                                    </button>
                                  ) : (
                                    <span className="row-actions">
                                      <IconButton
                                        size="sm"
                                        icon={<EditIcon />}
                                        label="Edit inspection"
                                        onClick={() => handleStartEditInspection(inspection)}
                                        disabled={busy}
                                      />
                                      <IconButton
                                        size="sm"
                                        tone="danger"
                                        icon={<TrashIcon />}
                                        label="Delete inspection"
                                        disabled={busy}
                                        onClick={async () => {
                                          const ok = await confirm({
                                            title: 'Delete this inspection?',
                                            message: (
                                              <>
                                                <strong>{inspectionLabel(inspection)} – {inspection.description}</strong> will be removed from the history.
                                              </>
                                            ),
                                            confirmLabel: 'Delete',
                                            tone: 'danger',
                                          });
                                          if (ok) handleDeleteInspection(inspection.id);
                                        }}
                                      />
                                    </span>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {!isReadOnly && (
                    <p className="construction-tab__secondary">
                      <button type="button" className="link-btn" onClick={() => setIsManualInspectionOpen(true)}>
                        Record one done on paper or by email
                      </button>
                    </p>
                  )}
                </div>
              )}

              {activeTab === 'settings' && projectDetail && (
                <div className="settings-tab">
                  {!stageId && (
                    <>
                      <PanelHeading title="Invoicing" />
                      <div className="pdm-grid pdm-grid--2">
                        <Field
                          label="Invoice care-of"
                          htmlFor="pdm-care-of"
                          className="span-all"
                          saveState={fieldSaveState('project:care_of')}
                          hint="The owner the invoice is addressed to (the paying party). Our client is shown beneath it as “c/-”. Leave blank to address the client directly."
                        >
                          <TextInput
                            id="pdm-care-of"
                            value={formData.care_of}
                            onChange={(e) => handleInputChange('care_of', e.target.value)}
                            placeholder="Owner the invoice is addressed to"
                          />
                        </Field>
                      </div>

                      <PanelHeading spaced title="Record" />
                      <dl className="field-list">
                        {renderFact('Job number', projectDetail.job_number ?? null, true)}
                        {renderFact('Created', projectDetail.created_at ? new Date(projectDetail.created_at).toLocaleDateString('en-AU') : null)}
                        {renderFact('Last updated', projectDetail.updated_at ? new Date(projectDetail.updated_at).toLocaleDateString('en-AU') : null)}
                      </dl>
                    </>
                  )}

                  {stageId && currentStage && (
                    <>
                      <PanelHeading title="Invoicing" />
                      <div className="pdm-grid pdm-grid--2">
                        <Field
                          label="Invoice care-of"
                          htmlFor="pdm-stage-care-of"
                          className="span-all"
                          saveState={fieldSaveState('stage:care_of')}
                          hint="The owner the invoice is addressed to (the paying party). Leave blank to address the client directly."
                        >
                          <TextInput
                            id="pdm-stage-care-of"
                            value={stageFormData.care_of}
                            onChange={(e) => handleStageInputChange('care_of', e.target.value)}
                            placeholder="Owner the invoice is addressed to"
                          />
                        </Field>
                      </div>

                      <PanelHeading spaced title="Record" />
                      <dl className="field-list">
                        {renderFact('Job number', currentStage.job_number, true)}
                        {renderFact('Stage ID', currentStage.stage_id, true)}
                        {renderFact('Description', currentStage.stage_description)}
                        {renderFact('Status', stageStatusName ? <WorkflowStatusPill status={stageStatusName} /> : null)}
                        {renderFact('Created', new Date(currentStage.created_at).toLocaleDateString('en-AU'))}
                      </dl>
                    </>
                  )}

                  {/* Documents live in their own tab, which lists existing
                      documents and creates new ones in place. */}

                  <PanelHeading spaced title="Danger zone" />
                  <div className="danger-card">
                    <div className="danger-card__text">
                      <p className="danger-card__title">{stageId ? 'Delete this stage' : 'Delete this project'}</p>
                      <p className="danger-card__body">
                        {stageId
                          ? 'Removes the stage and everything recorded against it: its documents, inspections, team and notes. This cannot be undone.'
                          : 'Removes the project and everything recorded against it: its stages, documents, inspections, fees, team and notes. This cannot be undone.'}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="pill-btn pill-btn--danger"
                      onClick={() => { void handleDeleteRequest(); }}
                      disabled={isDeleting || saveStatus === 'saving'}
                    >
                      {isDeleting ? 'Deleting…' : stageId ? 'Delete stage' : 'Delete project'}
                    </button>
                  </div>
                </div>
              )}
            </form>
          )}
        </div>
      </TabbedModalLayout>

    </Modal>

    {/* Naming a new site instruction, raised from Construction Admin. */}
    {createDocumentDialog}
    {/* Delete and row-removal questions. After the modal in the tree, so it is
        the last overlay in the document and therefore the frontmost one. */}
    {confirmDialog}
      {/* The paper/email inspection form. Secondary, and behind a click:
          these are the handover cases now that an inspection is normally
          recorded by issuing a site instruction. */}
      {isManualInspectionOpen && (
        <Modal
          isOpen
          onClose={() => setIsManualInspectionOpen(false)}
          title="Record an inspection"
          size="md"
          hasFooter
          footerContent={
            <div className="pill-row">
              <button type="button" className="pill-btn pill-btn--ghost" onClick={() => setIsManualInspectionOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="pill-btn pill-btn--primary"
                onClick={async () => {
                  // Only on success: a failure leaves the modal up with the
                  // reason inside it and what was typed still there.
                  if (await handleAddInspection()) setIsManualInspectionOpen(false);
                }}
                disabled={isAddingInspection || !inspectionFormData.inspection_date || !inspectionFormData.team_member_id || !inspectionFormData.description.trim()}
              >
                {isAddingInspection ? 'Recording...' : 'Record Inspection'}
              </button>
            </div>
          }
        >
          <p className="manual-inspection-hint">
            For an inspection carried out on paper or by email. It takes the next
            SI number, so it sits in the same sequence as the site inspection records.
          </p>
          <div className="pdm-grid pdm-grid--3 form-ui">
            <Field label="SI No" htmlFor="mi-si" hint={nextInspectionNumber != null ? `Next in sequence: ${nextInspectionNumber}` : 'Allocated automatically'}>
              <TextInput
                id="mi-si"
                type="number"
                min={1}
                mono
                value={inspectionFormData.sequence_number}
                onChange={(e) => setInspectionFormData(prev => ({ ...prev, sequence_number: e.target.value }))}
                placeholder={nextInspectionNumber != null ? String(nextInspectionNumber) : 'Auto'}
              />
            </Field>
            <Field label="Date" required htmlFor="mi-date">
              <TextInput
                id="mi-date"
                type="date"
                value={inspectionFormData.inspection_date}
                onChange={(e) => setInspectionFormData(prev => ({ ...prev, inspection_date: e.target.value }))}
              />
            </Field>
            <Field label="Engineer" required>
              <Dropdown
                items={activeEngineers.map(eng => ({
                  id: eng.id,
                  label: eng.full_name,
                  value: eng.id
                }))}
                value={inspectionFormData.team_member_id}
                onChange={(value) => setInspectionFormData(prev => ({ ...prev, team_member_id: value }))}
                placeholder="Select engineer..."
                searchable={true}
              />
            </Field>
            {/* One line, not a textarea: this is the inspection's name --
                "Footing inspection" -- and it becomes the SI title wherever
                the record is listed. */}
            <Field label="Description" required htmlFor="mi-desc" className="span-all">
              <TextInput
                id="mi-desc"
                value={inspectionFormData.description}
                onChange={(e) => setInspectionFormData(prev => ({ ...prev, description: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isAddingInspection
                      && inspectionFormData.inspection_date
                      && inspectionFormData.team_member_id
                      && inspectionFormData.description.trim()) {
                    e.preventDefault();
                    void handleAddInspection().then(ok => {
                      if (ok) setIsManualInspectionOpen(false);
                    });
                  }
                }}
                placeholder="e.g. Footing inspection"
              />
            </Field>
          </div>
          {inspectionError && <p className="field__error" role="alert">{inspectionError}</p>}
        </Modal>
      )}
    </>
  );
};

export default ProjectDetailModal;

